import { appConfirm } from '../../services/appDialogs';
import { AppSelect } from '../ui/AppSelect';
import { VIDEO_PROVIDER_VOLCENGINE_SEEDANCE, DOMESTIC_SEEDANCE_MODELS, getDomesticSeedanceModel, getDomesticSeedanceResolutions, normalizeDomesticSeedanceResolution, isDomesticOfficialSeedance } from '../../utils/domesticSeedanceCapabilities';
import { startTransition, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, BookOpen, Clapperboard, Download, Film, Loader2, PackagePlus, RefreshCw, Sparkles } from "lucide-react";
import { authenticatedFetch } from "../../utils/api";
import { API_HOST } from "../../utils/constants";
import { useTheme } from "../../context/ThemeContext";
import { useToast } from "../../context/ToastContext";
import { useStyle } from "../../context/StyleContext";
import { startPolling, startPostProcessPolling, submitEraseSubtitle, submitVideoTask } from "../../services/videoService";
import { applyLighting } from "../storyboard/common/LightingPresets";
import { STYLE_OPTIONS } from "../storyboard/common/Constants";
import { resolveRefMarkers } from "../../utils/mentionUtils";
import { useAssetStatusMap } from "../../hooks/useAssetStatusMap";
import { getCurrentImageProvider, normalizeWanxImageQualityTier } from "../../utils/wanxImageCapabilities";
import { isDatastartImageProvider } from '../../constants/datastartImageModels';
import { isAstraflowImageProvider } from '../../constants/astraflowImageModels';
import {
  getProjectDefaultVideoModel,
  getProjectDefaultVideoRatio,
  getProjectVideoAssetApiBase,
  getProjectVideoSubmissionProvider,
  getVideoResolutionsForProvider,
  getVideoProviderLabel,
  isSeedanceVideoProvider,
  normalizeVideoResolutionForProvider,
  supportsVideoAssetIngest,
} from "../../utils/projectRuntime";
import {
  applyAssetCreateResult,
  assetLibraryLabel,
  clearVideoAssetBinding,
  getEffectiveAssetMeta,
  getVideoAssetFieldNames,
  isSameVideoAssetLibrary,
} from "../../utils/guaVideoAssetStatus";
import {
  mergePreparedVideoAssetMetadata,
  pollVideoAssetUntilTerminal,
  preflightVideoAssetContents,
  sanitizeVideoAuxiliaryReferenceItems,
} from "../../utils/videoAssetPreflight";
import {
  SURFACE, REF_LIMIT, BOARD_COUNT, BOARD_SECONDS,
  GUA_VIDEO_RESOLUTION, NO_SUBTITLE_PROMPT,
  DEFAULT_VIDEO_RATIO,
} from "./episode-storyboard-board/constants";
import {
  firstText, detectDirectorStoryboardIntent,
  normalizeImageUrl, getAssetImageUrl,
  assetMatchesText,
  loadMemory, saveMemory,
  getAssetKey, buildSelectedRefsFromKeys,
  createPlaceholderBoards, mergeBoardIntoList,
  getAssetMetaForUrl,
  normalizeVideoPackageItems, applyGuaMetaToPackageItems, buildAssetVideoPackageItems,
  ensureVideoDraft, buildBoardVideoPrompt, buildGuaVideoContents,
  normalizeVideoDuration, summarizeImageRefs,
  buildManualStoryboardSegments, normalizeManualSegments,
  normalizePresentationMode,
} from "./episode-storyboard-board/utils";
import {
  loadStoryboardBoardState,
  loadStoryboardBoardVideos,
  pollStoryboardBoardTask,
  postStoryboardBoardStream,
  regenerateStoryboardBoard,
  resolveStoryboardBoardRequest,
  saveStoryboardBoardState,
  updateStoryboardBoard,
  updateStoryboardBoardVideo,
} from "./episode-storyboard-board/streamApi";
import PreviewModal from "./episode-storyboard-board/PreviewModal";
import StoryboardBoardListItem from "./episode-storyboard-board/StoryboardBoardCard";
import AssetPickerModal from "./episode-storyboard-board/AssetPickerModal";
import { uploadFileToCos } from "../../utils/fileUtils";
import ManualSegmentationPanel from "./episode-storyboard-board/ManualSegmentationPanel";
import HoverImagePreview from "./episode-storyboard-board/HoverImagePreview";
import SelectedRefsPanel from "./episode-storyboard-board/SelectedRefsPanel";
import EstimatedPoints from "../common/EstimatedPoints";
import {
  SEEDANCE25_PROVIDER_OPTIONS,
  buildSeedance25ReferencePayload,
  getSeedance25ReferenceMediaType,
  supportsSeedance25MediaReferences,
  normalizeSeedance25ReferencePrompt,
  getSeedance25Capabilities,
  isSeedance25Provider,
  normalizeSeedance25Duration,
  normalizeSeedance25SubmissionProvider,
  resolveSeedance25Provider,
  normalizeSeedance25Ratio,
  normalizeSeedance25Resolution,
  validateSeedance25References,
} from "../../utils/seedance25Capabilities";

const useStableEvent = (handler) => {
  const handlerRef = useRef(handler);
  useEffect(() => {
    handlerRef.current = handler;
  }, [handler]);
  return useCallback((...args) => handlerRef.current?.(...args), []);
};

// 故事板生图接口仍只接收 REF_LIMIT 张；视频输入包可以按当前 2.5
// 厂商能力保留更多图片。两条链路必须分开裁剪，避免选择 17-50 张后
// 先把故事板生图请求打失败。
const limitSelectedReferenceGroups = (groups = {}, limit = REF_LIMIT) => {
  const remaining = Math.max(0, Number(limit) || REF_LIMIT);
  const result = { characters: [], scenes: [], props: [] };
  let count = 0;
  ["characters", "scenes", "props"].forEach((key) => {
    if (count >= remaining) return;
    const available = Array.isArray(groups?.[key]) ? groups[key] : [];
    result[key] = available.slice(0, remaining - count);
    count += result[key].length;
  });
  return result;
};

const StoryboardScriptGate = ({ projectId, episodeId, onRefresh }) => {
  const navigate = useNavigate();
  return (
    <div className="flex flex-1 items-center justify-center p-6">
      <div className="w-full max-w-[780px] rounded-md border p-6 text-center" style={{ borderColor: "var(--color-border-primary)", backgroundColor: SURFACE.panel }}>
        <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-md" style={{ backgroundColor: "var(--color-accent-light)", color: "var(--color-accent-primary)" }}>
          <BookOpen size={24} />
        </div>
        <div className="text-lg font-bold" style={{ color: "var(--color-text-primary)" }}>当前集缺少正式剧本</div>
        <div className="mx-auto mt-2 max-w-[560px] text-sm leading-6" style={{ color: "var(--color-text-muted)" }}>
          故事板导演台需要读取本集剧本来保持人物、场景和节奏连续。请先在剧本中心导入总剧本并按集拆分。
        </div>
        <div className="mt-5 flex flex-wrap items-center justify-center gap-2">
          <button
            type="button"
            onClick={() => navigate(`/project/${projectId}/script-analysis`)}
            className="h-9 px-4 rounded-md border text-sm font-semibold inline-flex items-center gap-2 cursor-pointer"
            style={{ borderColor: "var(--color-border-primary)", backgroundColor: SURFACE.card, color: "var(--color-text-primary)" }}
          >
            <BookOpen size={15} />
            去剧本中心拆剧本
          </button>
          {episodeId && (
            <button
              type="button"
              onClick={onRefresh}
              className="h-9 px-4 rounded-md border text-sm font-semibold inline-flex items-center gap-2 cursor-pointer"
              style={{ borderColor: "var(--color-border-primary)", backgroundColor: SURFACE.card, color: "var(--color-text-primary)" }}
            >
              <RefreshCw size={15} />
              重新检查
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

const StoryboardBoardLoadingState = () => (
  <div className="flex flex-1 items-center justify-center p-6">
    <div className="w-full max-w-[860px] rounded-md border p-6" style={{ borderColor: "var(--color-border-primary)", backgroundColor: SURFACE.panel }}>
      <div className="flex items-center gap-3">
        <span className="flex h-10 w-10 items-center justify-center rounded-md" style={{ backgroundColor: "var(--color-accent-light)", color: "var(--color-accent-primary)" }}>
          <Loader2 size={20} className="animate-spin" />
        </span>
        <div>
          <div className="text-base font-bold" style={{ color: "var(--color-text-primary)" }}>正在加载故事板导演台</div>
          <div className="mt-1 text-sm" style={{ color: "var(--color-text-muted)" }}>正在读取本集剧本、参考图和已生成故事板。</div>
        </div>
      </div>
      <div className="mt-6 grid grid-cols-1 gap-3 md:grid-cols-3">
        {[0, 1, 2].map((item) => (
          <div key={item} className="h-28 animate-pulse animate-in fade-in duration-300 rounded-md" style={{ backgroundColor: SURFACE.card, border: "1px solid var(--color-border-primary)" }} />
        ))}
      </div>
      <div className="mt-3 h-40 animate-pulse animate-in fade-in duration-300 rounded-md" style={{ backgroundColor: SURFACE.card, border: "1px solid var(--color-border-primary)" }} />
    </div>
  </div>
);

const VIDEO_BUSY_STATUSES = new Set(["submitting", "processing", "queued", "polling"]);
const STORYBOARD_TASK_TERMINAL_STATUSES = new Set(["success", "failed", "canceled", "cancelled", "not_found"]);
const FOCUS_COLUMN_DEFAULTS = [55, 20, 25];

const normalizeFocusColumnPercents = (value) => {
  if (!Array.isArray(value) || value.length !== 3) return FOCUS_COLUMN_DEFAULTS;
  const numbers = value.map((item) => Number(item));
  if (numbers.some((item) => !Number.isFinite(item) || item <= 0)) return FOCUS_COLUMN_DEFAULTS;
  const total = numbers.reduce((sum, item) => sum + item, 0);
  if (!Number.isFinite(total) || total <= 0) return FOCUS_COLUMN_DEFAULTS;
  return numbers.map((item) => Math.round((item / total) * 10000) / 100);
};

const isStoryboardGenerationTaskActive = (task) => Boolean(
  task?.taskId && !STORYBOARD_TASK_TERMINAL_STATUSES.has(firstText(task.status, "processing"))
);

const ensureStoryboardBoardSlots = (current = [], count = 0, duration = BOARD_SECONDS) => {
  const list = Array.isArray(current) ? current : [];
  const byIndex = new Map();
  let maxIndex = 0;
  list.forEach((board, position) => {
    const index = Number(board?.index) || position + 1;
    if (!index) return;
    maxIndex = Math.max(maxIndex, index);
    byIndex.set(index, { ...(board || {}), index });
  });
  const slotCount = Math.max(Number(count) || 0, maxIndex, list.length);
  if (slotCount <= 0) return [];
  return createPlaceholderBoards(slotCount, duration).map((placeholder) => {
    const index = Number(placeholder.index);
    const existing = byIndex.get(index);
    if (!existing) return placeholder;
    const status = firstText(
      existing.status,
      normalizeImageUrl(existing.imageUrl) ? "success" : placeholder.status
    );
    return {
      ...placeholder,
      ...existing,
      index,
      status,
    };
  });
};

const markUnfinishedStoryboardBoardsFailed = (current = [], count = 0, duration = BOARD_SECONDS, errorMessage = "") => (
  ensureStoryboardBoardSlots(current, count, duration).map((board) => {
    const status = firstText(board?.status);
    if (status === "success" || status === "failed" || normalizeImageUrl(board?.imageUrl)) {
      return board;
    }
    return {
      ...board,
      status: "failed",
      error: firstText(errorMessage, board?.error, "生成失败"),
    };
  })
);

const STORYBOARD_HISTORY_BLOCK_PATTERNS = [
  /GuaVideo/i,
  /BAD_REQUEST/i,
  /Submit Error/i,
  /Request id/i,
  /视频提交失败/,
  /视频已生成/,
  /视频生成完成/,
  /已提交\s*GuaVideo/,
  /用第\d+张故事板生成\s*\d+\s*秒视频/,
  /生成\s*\d+\s*秒视频/,
];

const normalizeVideoErrorMessage = (message) => {
  const text = firstText(message, "视频生成失败");
  if (/output video may be related to copyright restrictions/i.test(text) || /copyright restrictions/i.test(text)) {
    return "生成失败：返回视频内容可能有侵权风险";
  }
  return text;
};

const buildStoryboardConversationHistory = (items = []) => items
  .filter((message) => message?.role === "user")
  .map((message) => ({
    role: "user",
    content: firstText(message.content).slice(0, 800),
    createdAt: message.createdAt,
  }))
  .filter((message) => message.content && !STORYBOARD_HISTORY_BLOCK_PATTERNS.some((pattern) => pattern.test(message.content)))
  .slice(-8);

const sanitizeFileNamePart = (value, fallback = "未命名") => firstText(value, fallback)
  .replace(/[\\/:*?"<>|]/g, "_")
  .replace(/\s+/g, "")
  .replace(/_+/g, "_")
  .slice(0, 80) || fallback;

const videoFileExtension = (url) => {
  const clean = firstText(url).split("?")[0].toLowerCase();
  const match = clean.match(/\.(mp4|mov|webm|m4v)$/);
  return match ? match[0] : ".mp4";
};

const getBoardVideoUrl = (video) => firstText(video?.videoUrl, video?.url, video?.result?.videoUrl, video?.result?.video_url);

const isBoardVideoBusy = (video) => VIDEO_BUSY_STATUSES.has(video?.status);

const BOARD_HISTORY_LIMIT = 24;

const normalizeHistoryDate = (value) => firstText(value, new Date().toISOString());

const buildBoardImageHistoryEntry = (board, label = "") => {
  const imageUrl = normalizeImageUrl(board?.imageUrl || board?.url);
  const boardIndex = Number(board?.index || board?.boardIndex);
  if (!imageUrl || !boardIndex) return null;
  const createdAt = normalizeHistoryDate(board?.completedAt || board?.updatedAt || board?.createdAt);
  return {
    id: firstText(board?.historyId, `${boardIndex}-${createdAt}-${imageUrl.slice(-36)}`),
    boardIndex,
    imageUrl,
    url: imageUrl,
    title: firstText(board?.title, `第${boardIndex}张故事板`),
    prompt: firstText(board?.prompt),
    segmentText: firstText(board?.segmentText),
    timeRange: firstText(board?.timeRange),
    durationSeconds: board?.durationSeconds,
    referenceCounts: board?.referenceCounts,
    referenceSnapshot: board?.referenceSnapshot,
    openaiGeneration: board?.openaiGeneration,
    emotionPlan: board?.emotionPlan,
    blockingPlan: board?.blockingPlan,
    presentationMode: board?.presentationMode,
    label: firstText(label, board?.historyLabel, "故事板整图"),
    createdAt,
  };
};

const buildVideoHistoryEntry = (video, boardIndex, label = "") => {
  const videoUrl = getBoardVideoUrl(video);
  const index = Number(boardIndex || video?.boardIndex || video?.index);
  if (!videoUrl || !index) return null;
  const createdAt = normalizeHistoryDate(video?.completedAt || video?.updatedAt || video?.createdAt);
  const { history: _history, ...rest } = video || {};
  return {
    ...rest,
    id: firstText(video?.historyId, video?.taskId ? `${index}-${video.taskId}` : `${index}-${createdAt}-${videoUrl.slice(-36)}`),
    boardIndex: index,
    videoUrl,
    url: videoUrl,
    label: firstText(label, video?.historyLabel, "视频版本"),
    status: "success",
    progress: 100,
    createdAt,
  };
};

const mergeHistoryEntries = (history = [], entries = [], keyGetter = (item) => firstText(item?.url)) => {
  const seen = new Set();
  return [...entries, ...(Array.isArray(history) ? history : [])]
    .filter(Boolean)
    .filter((item) => {
      const key = keyGetter(item);
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .slice(0, BOARD_HISTORY_LIMIT);
};

const archiveBoardImageVersions = (histories = {}, boards = [], label = "整集重排前") => {
  const next = Object.fromEntries(
    Object.entries(histories || {}).map(([key, entries]) => [key, Array.isArray(entries) ? [...entries] : []])
  );
  (Array.isArray(boards) ? boards : []).forEach((board) => {
    const boardIndex = Number(board?.index || board?.boardIndex);
    const entry = buildBoardImageHistoryEntry(board, label);
    if (!boardIndex || !entry) return;
    next[boardIndex] = mergeHistoryEntries(
      next[boardIndex],
      [entry],
      (item) => normalizeImageUrl(item?.imageUrl || item?.url)
    );
  });
  return next;
};

const archiveBoardVideoVersions = (videos = {}, label = "整集重排前") => {
  const next = {};
  Object.entries(videos || {}).forEach(([rawBoardIndex, video]) => {
    const boardIndex = Number(rawBoardIndex || video?.boardIndex || video?.index);
    if (!boardIndex) return;
    const currentEntry = buildVideoHistoryEntry(video, boardIndex, label);
    const history = mergeHistoryEntries(
      video?.history,
      currentEntry ? [currentEntry] : [],
      (item) => firstText(item?.videoUrl, item?.url)
    );
    if (!history.length) return;
    next[boardIndex] = {
      boardIndex,
      status: "archived",
      progress: 0,
      history,
      updatedAt: new Date().toISOString(),
    };
  });
  return next;
};

const mergeBoardVideoMaps = (...maps) => {
  const next = {};
  maps.forEach((map) => {
    Object.entries(map && typeof map === "object" ? map : {}).forEach(([rawBoardIndex, incoming]) => {
      const boardIndex = Number(rawBoardIndex || incoming?.boardIndex || incoming?.index);
      if (!boardIndex || !incoming || typeof incoming !== "object") return;
      const previous = next[boardIndex] || {};
      const previousCurrent = buildVideoHistoryEntry(previous, boardIndex, "恢复的前一版");
      const incomingUrl = getBoardVideoUrl(incoming);
      const previousUrl = getBoardVideoUrl(previous);
      const history = mergeHistoryEntries(
        [
          ...(Array.isArray(incoming?.history) ? incoming.history : []),
          ...(Array.isArray(previous?.history) ? previous.history : []),
        ],
        previousCurrent && incomingUrl && previousUrl !== incomingUrl ? [previousCurrent] : [],
        (item) => firstText(item?.videoUrl, item?.url)
      );
      next[boardIndex] = {
        ...previous,
        ...incoming,
        boardIndex,
        ...(history.length ? { history } : {}),
      };
    });
  });
  return next;
};

const mergeBoardImageHistoryMaps = (...maps) => {
  const next = {};
  maps.forEach((map) => {
    Object.entries(map && typeof map === "object" ? map : {}).forEach(([rawBoardIndex, entries]) => {
      const boardIndex = Number(rawBoardIndex);
      if (!boardIndex || !Array.isArray(entries)) return;
      next[boardIndex] = mergeHistoryEntries(
        next[boardIndex],
        entries,
        (item) => normalizeImageUrl(item?.imageUrl || item?.url)
      );
    });
  });
  return next;
};

const STORYBOARD_PRESENTATION_MODE_OPTIONS = [
  { value: "formal", label: "正式图", title: "生成偏真人制作板，用于最终视觉预览" },
  { value: "narration", label: "解说", title: "按解说剧拆板，旁白贯穿剧情" },
  { value: "blocking", label: "模糊站位", title: "黑白线稿，只锁定分镜、站位、视线和镜头轴线" },
  { value: "grid9", label: "九宫格", title: "黑白线稿无文字3x3连续分镜，第9格作为下一张开端锚点" },
];

const buildEpisodeDownloadLabel = (episode, fallbackEpisodeId) => {
  const order = episode?.sortOrder || episode?.episodeNumber;
  const rawTitle = firstText(episode?.title, episode?.name, fallbackEpisodeId ? `第${fallbackEpisodeId}集` : "当前集");
  if (!order) return rawTitle;
  const cleanTitle = rawTitle
    .replace(new RegExp(`^第\\s*${order}\\s*集\\s*[：:·\\-—_\\s]*`), "")
    .replace(/^第\s*\d+\s*集\s*[：:·\-—_\s]*/, "")
    .trim();
  return cleanTitle ? `第${order}集 ${cleanTitle}` : `第${order}集`;
};





const EpisodeStoryboardBoardWorkbench = ({ projectId, episodeId }) => {
  const toast = useToast();
  const { getTheme } = useTheme();
  const { currentStyle } = useStyle();
  const theme = getTheme();
  const chatEndRef = useRef(null);
  const boardGenerationPollingRef = useRef(null);
  const videoPollingMapRef = useRef(new Map());
  const stoppedVideoTaskIdsRef = useRef(new Set());
  const stoppedVideoBoardIndexesRef = useRef(new Set());
  const saveStateTimerRef = useRef(null);
  const stateSaveChainsRef = useRef(new Map());
  const assetLoadRequestRef = useRef(0);
  const dataLoadRequestRef = useRef(0);
  const scopeKey = `${projectId || ""}:${episodeId || ""}`;
  const scopeKeyRef = useRef(scopeKey);
  const hydratedScopeRef = useRef("");
  scopeKeyRef.current = scopeKey;
  const { assetMetaMap, refreshStatusMap } = useAssetStatusMap(projectId, { auto: false });

  const [episode, setEpisode] = useState(null);
  const [projectTitle, setProjectTitle] = useState("");
  const [projectStylePrompt, setProjectStylePrompt] = useState("");
  const [assets, setAssets] = useState({ characters: [], scenes: [], props: [] });
  const [selectedRefKeys, setSelectedRefKeys] = useState(() => new Set());
  const selectedRefKeysRef = useRef(selectedRefKeys);
  const [messages, setMessages] = useState([]);
  const [directorRequest, setDirectorRequest] = useState("");
  const [boards, setBoards] = useState([]);
  const [boardImageHistories, setBoardImageHistories] = useState({});
  const [activeBoardIndex, setActiveBoardIndex] = useState(1);
  const [aspectRatio, setAspectRatio] = useState("16:9");
  const [globalVideoResolution, setGlobalVideoResolution] = useState(GUA_VIDEO_RESOLUTION);
  const [loading, setLoading] = useState(projectId && episodeId ? "load" : "");
  const [regeneratingBoardIndexes, setRegeneratingBoardIndexes] = useState(() => new Set());
  const [generatingVideoBoardIndexes, setGeneratingVideoBoardIndexes] = useState(() => new Set());
  const [openVideoOptionsIndexes, setOpenVideoOptionsIndexes] = useState(() => new Set());
  const [boardVideos, setBoardVideos] = useState({});
  const [videoDrafts, setVideoDrafts] = useState({});
  const [activeBoardGenerationTask, setActiveBoardGenerationTask] = useState(null);
  const [boardGenerationResumeNonce, setBoardGenerationResumeNonce] = useState(0);
  const activeBoardGenerationTaskRef = useRef(activeBoardGenerationTask);
  const [pendingDirectorAction, setPendingDirectorAction] = useState(null);
  const [segmentationMode, setSegmentationMode] = useState("ai");
  const [presentationMode, setPresentationModeState] = useState("formal");
  const [manualSegments, setManualSegments] = useState([]);
  const [memoryReady, setMemoryReady] = useState(false);
  const [error, setError] = useState("");
  const [previewImage, setPreviewImage] = useState(null);
  const [uploadingAssetUrls, setUploadingAssetUrls] = useState(() => new Set());
  const [uploadingAllBoardAssets, setUploadingAllBoardAssets] = useState(false);
  const [generatingAllVideos, setGeneratingAllVideos] = useState(false);
  const [downloadingAllVideos, setDownloadingAllVideos] = useState(false);
  const [assetsLoading, setAssetsLoading] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [videoReferencePickerBoardIndex, setVideoReferencePickerBoardIndex] = useState(null);
  const [uploadingVideoReferences, setUploadingVideoReferences] = useState(false);
  const [projectRuntimeTick, setProjectRuntimeTick] = useState(0);
  const [videoProviderOverride, setVideoProviderOverride] = useState("");
  const [officialDomesticModel, setOfficialDomesticModel] = useState(DOMESTIC_SEEDANCE_MODELS[0].id);
  const [focusColumnPercents, setFocusColumnPercents] = useState(FOCUS_COLUMN_DEFAULTS);
  activeBoardGenerationTaskRef.current = activeBoardGenerationTask;

  const projectRuntimeVideoProvider = useMemo(
    () => {
      void projectRuntimeTick;
      return getProjectVideoSubmissionProvider(projectId, "guavideo");
    },
    [projectId, projectRuntimeTick]
  );
  const videoProvider = isDomesticOfficialSeedance(videoProviderOverride) ? VIDEO_PROVIDER_VOLCENGINE_SEEDANCE : normalizeSeedance25SubmissionProvider(videoProviderOverride) || projectRuntimeVideoProvider;
  const seedance25Capabilities = getSeedance25Capabilities(videoProvider);
  const isSeedance25 = Boolean(seedance25Capabilities);
  const videoResolutionOptions = useMemo(
    () => isDomesticOfficialSeedance(videoProvider) ? getDomesticSeedanceResolutions(officialDomesticModel) : seedance25Capabilities?.resolutions || getVideoResolutionsForProvider(videoProvider),
    [seedance25Capabilities, videoProvider, officialDomesticModel],
  );
  const hasSingleVideoResolution = seedance25Capabilities
    ? seedance25Capabilities.resolutions.length <= 1
    : videoResolutionOptions.length <= 1;
  // 整集参考图参与故事板生图，后端固定最多 16 张；2.5 厂商更高的
  // 图片上限只应用到每张故事板自己的视频输入包。
  const referenceLimit = REF_LIMIT;
  const videoReferenceLimit = isSeedance25 ? seedance25Capabilities.maxReferenceImages : 9;
  const videoAssetApiBase = useMemo(
    () => {
      void projectRuntimeTick;
      if (isSeedance25Provider(videoProvider)) return null;
      return getProjectVideoAssetApiBase(projectId, "guavideo");
    },
    [projectId, projectRuntimeTick, videoProvider]
  );
  const assetIngestSupported = supportsVideoAssetIngest(videoProvider);
  const videoProviderLabel = getVideoProviderLabel(videoProvider);
  const projectVideoModel = useMemo(
    () => {
      void projectRuntimeTick;
      if (seedance25Capabilities) return seedance25Capabilities.model;
      if (isDomesticOfficialSeedance(videoProvider)) return officialDomesticModel;
      return getProjectDefaultVideoModel(projectId, false);
    },
    [projectId, projectRuntimeTick, seedance25Capabilities, videoProvider, officialDomesticModel]
  );
  const projectDefaultVideoRatio = useMemo(
    () => {
      void projectRuntimeTick;
      return getProjectDefaultVideoRatio(projectId, DEFAULT_VIDEO_RATIO);
    },
    [projectId, projectRuntimeTick]
  );

  const scriptText = episode?.scriptContent || "";
  const normalizedManualSegments = useMemo(
    () => normalizeManualSegments(manualSegments, BOARD_SECONDS),
    [manualSegments]
  );

  useEffect(() => {
    selectedRefKeysRef.current = selectedRefKeys;
  }, [selectedRefKeys]);

  useEffect(() => {
    const onProfileChange = (event) => {
      if (!event?.detail?.projectId || String(event.detail.projectId) === String(projectId)) {
        setProjectRuntimeTick((value) => value + 1);
      }
    };
    window.addEventListener("project-runtime-profile-change", onProfileChange);
    return () => window.removeEventListener("project-runtime-profile-change", onProfileChange);
  }, [projectId]);

  const selectedRefs = useMemo(
    () => buildSelectedRefsFromKeys(assets, selectedRefKeys),
    [assets, selectedRefKeys]
  );
  const storyboardStylePrompt = useMemo(() => {
    const styleOption = STYLE_OPTIONS.find((item) => item.value === currentStyle);
    const styleLabel = firstText(styleOption?.label, currentStyle, "未指定");
    const projectStyle = firstText(projectStylePrompt);
    const styleAnchor = `当前画风类型：${styleLabel}（${currentStyle || "unknown"}）`;
    return projectStyle ? `${styleAnchor}\n项目风格要求：${projectStyle}` : styleAnchor;
  }, [currentStyle, projectStylePrompt]);

  const selectedRefCount = selectedRefs.characters.length + selectedRefs.scenes.length + selectedRefs.props.length;
  const initialGenerating = loading === "generate" || isStoryboardGenerationTaskActive(activeBoardGenerationTask);
  const itemTaskBusy = regeneratingBoardIndexes.size > 0 || generatingVideoBoardIndexes.size > 0;
  const scriptMissing = loading !== "load" && (!episode || !scriptText.trim());
  const initialLoading = loading === "load" && !episode && !boards.length;
  const directorBusy = initialGenerating;
  const fullGenerationBusy = initialGenerating || itemTaskBusy;
  const hasGeneratedBoards = useMemo(
    () => boards.some((board) => normalizeImageUrl(board?.imageUrl) || ["success", "failed"].includes(board?.status)),
    [boards]
  );
  const videoGeneratableBoards = useMemo(
    () => boards
      .filter((board) => {
        const boardIndex = Number(board?.index);
        if (!boardIndex || !normalizeImageUrl(board?.imageUrl)) return false;
        if (regeneratingBoardIndexes.has(boardIndex) || generatingVideoBoardIndexes.has(boardIndex)) return false;
        const video = boardVideos?.[boardIndex];
        if (isBoardVideoBusy(video)) return false;
        return !(video?.status === "success" && getBoardVideoUrl(video));
      })
      .sort((a, b) => Number(a?.index || 0) - Number(b?.index || 0)),
    [boardVideos, boards, generatingVideoBoardIndexes, regeneratingBoardIndexes]
  );
  const downloadableVideos = useMemo(
    () => Object.entries(boardVideos || {})
      .map(([rawBoardIndex, video]) => ({
        boardIndex: Number(rawBoardIndex),
        video,
        url: getBoardVideoUrl(video),
      }))
      .filter((item) => item.boardIndex && item.video?.status === "success" && item.url)
      .sort((a, b) => a.boardIndex - b.boardIndex),
    [boardVideos]
  );
  const previewableBoards = useMemo(
    () => boards
      .filter((board) => normalizeImageUrl(board?.imageUrl))
      .map((board) => ({
        boardIndex: Number(board?.index),
        url: normalizeImageUrl(board?.imageUrl),
        title: board?.title || `第${board?.index}张故事板`,
      })),
    [boards]
  );
  const currentPreviewPosition = useMemo(
    () => previewableBoards.findIndex((item) => Number(item.boardIndex) === Number(previewImage?.boardIndex)),
    [previewImage?.boardIndex, previewableBoards]
  );
  const showPreviewAt = useCallback((position) => {
    const target = previewableBoards[position];
    if (target) setPreviewImage(target);
  }, [previewableBoards]);
  const activeBoard = useMemo(() => (
    boards.find((board) => Number(board?.index) === Number(activeBoardIndex)) || boards[0] || null
  ), [activeBoardIndex, boards]);

  useEffect(() => {
    if (!boards.length) return;
    const exists = boards.some((board) => Number(board?.index) === Number(activeBoardIndex));
    if (!exists) setActiveBoardIndex(Number(boards[0]?.index) || 1);
  }, [activeBoardIndex, boards]);

  useEffect(() => {
    const boardIndex = Number(activeBoard?.index);
    if (!boardIndex) return;
    setOpenVideoOptionsIndexes((current) => {
      if (current.has(boardIndex)) return current;
      const next = new Set(current);
      next.add(boardIndex);
      return next;
    });
  }, [activeBoard?.index]);

  const memoryPayload = useMemo(() => ({
    selectedRefKeys: [...selectedRefKeys],
    messages,
    pendingDirectorAction,
    aspectRatio,
    globalVideoResolution,
    videoProviderOverride,
    officialDomesticModel,
    boardVideos,
    boardImageHistories,
    videoDrafts,
    activeBoardGenerationTask,
    openVideoOptionsIndexes: [...openVideoOptionsIndexes],
    segmentationMode,
    presentationMode,
    manualSegments: normalizedManualSegments,
    focusColumnPercents: normalizeFocusColumnPercents(focusColumnPercents),
  }), [selectedRefKeys, messages, pendingDirectorAction, aspectRatio, globalVideoResolution, videoProviderOverride, officialDomesticModel, boardVideos, boardImageHistories, videoDrafts, activeBoardGenerationTask, openVideoOptionsIndexes, segmentationMode, presentationMode, normalizedManualSegments, focusColumnPercents]);

  const updateFocusColumnPercents = useCallback((value) => {
    setFocusColumnPercents(normalizeFocusColumnPercents(value));
  }, []);

  const setPresentationMode = useCallback((mode) => {
    const nextMode = normalizePresentationMode(mode);
    setPresentationModeState(nextMode);
    if (segmentationMode === "manual" && scriptText.trim()) {
      setManualSegments(buildManualStoryboardSegments(scriptText, BOARD_COUNT, BOARD_SECONDS, nextMode));
    }
    setVideoDrafts((current) => {
      const next = {};
      Object.entries(current || {}).forEach(([key, draft]) => {
        next[key] = { ...(draft || {}), prompt: "", presentationMode: nextMode };
      });
      return next;
    });
  }, [scriptText, segmentationMode]);

  const mapPersistedVideos = useCallback((videos = []) => {
    const mapped = {};
    (Array.isArray(videos) ? videos : []).forEach((video) => {
      const boardIndex = Number(video?.boardIndex || video?.index);
      if (!boardIndex) return;
      mapped[boardIndex] = {
        ...(video || {}),
        status: firstText(video?.status, "processing"),
        videoUrl: firstText(video?.videoUrl, video?.url),
        url: firstText(video?.videoUrl, video?.url),
      };
    });
    return mapped;
  }, []);

  const queueStoryboardStateSave = useCallback((targetProjectId, targetEpisodeId, targetScopeKey, payload) => {
    if (!targetProjectId || !targetEpisodeId || !targetScopeKey) return Promise.resolve(null);
    const previousSave = stateSaveChainsRef.current.get(targetScopeKey) || Promise.resolve();
    const save = previousSave
      .catch(() => undefined)
      .then(() => saveStoryboardBoardState(targetProjectId, targetEpisodeId, payload));
    stateSaveChainsRef.current.set(targetScopeKey, save);
    const clearCompletedSave = () => {
      if (stateSaveChainsRef.current.get(targetScopeKey) === save) {
        stateSaveChainsRef.current.delete(targetScopeKey);
      }
    };
    save.then(clearCompletedSave, clearCompletedSave);
    return save;
  }, []);

  useEffect(() => {
    if (!projectId || !episodeId) return;
    const requestScopeKey = scopeKey;
    assetLoadRequestRef.current += 1;
    dataLoadRequestRef.current += 1;
    hydratedScopeRef.current = "";
    if (saveStateTimerRef.current) {
      clearTimeout(saveStateTimerRef.current);
      saveStateTimerRef.current = null;
    }
    boardGenerationPollingRef.current?.stop?.();
    boardGenerationPollingRef.current = null;
    videoPollingMapRef.current.forEach((stopPolling) => stopPolling?.());
    videoPollingMapRef.current.clear();
    stoppedVideoTaskIdsRef.current.clear();
    stoppedVideoBoardIndexesRef.current.clear();
    setGeneratingVideoBoardIndexes(new Set());
    setRegeneratingBoardIndexes(new Set());
    setBoards([]);
    setAssets({ characters: [], scenes: [], props: [] });
    setEpisode(null);
    setProjectTitle("");
    setProjectStylePrompt("");
    setDirectorRequest("");
    setError("");
    setPickerOpen(false);
    setVideoReferencePickerBoardIndex(null);
    setAssetsLoading(false);
    setActiveBoardIndex(1);
    setPreviewImage(null);
    setUploadingAssetUrls(new Set());
    setUploadingAllBoardAssets(false);
    setGeneratingAllVideos(false);
    setDownloadingAllVideos(false);
    setMemoryReady(false);
    setVideoProviderOverride("");
    let cancelled = false;
    const memory = loadMemory(projectId, episodeId);
    const memoryRefKeys = new Set(Array.isArray(memory.selectedRefKeys) ? memory.selectedRefKeys : []);
    selectedRefKeysRef.current = memoryRefKeys;
    setSelectedRefKeys(memoryRefKeys);
    setMessages(Array.isArray(memory.messages) ? memory.messages : []);
    setPendingDirectorAction(memory.pendingDirectorAction || null);
    setAspectRatio(firstText(memory.aspectRatio, "16:9"));
    setGlobalVideoResolution(firstText(memory.globalVideoResolution, GUA_VIDEO_RESOLUTION));
    setVideoProviderOverride(isDomesticOfficialSeedance(memory.videoProviderOverride) ? VIDEO_PROVIDER_VOLCENGINE_SEEDANCE : normalizeSeedance25SubmissionProvider(memory.videoProviderOverride));
    setBoardVideos(memory.boardVideos && typeof memory.boardVideos === "object" ? memory.boardVideos : {});
    setBoardImageHistories(memory.boardImageHistories && typeof memory.boardImageHistories === "object" ? memory.boardImageHistories : {});
    setVideoDrafts(memory.videoDrafts && typeof memory.videoDrafts === "object" ? memory.videoDrafts : {});
    setActiveBoardGenerationTask(memory.activeBoardGenerationTask && typeof memory.activeBoardGenerationTask === "object" ? memory.activeBoardGenerationTask : null);
    setSegmentationMode(memory.segmentationMode === "manual" ? "manual" : "ai");
    setPresentationModeState(normalizePresentationMode(memory.presentationMode));
    setManualSegments(Array.isArray(memory.manualSegments) ? normalizeManualSegments(memory.manualSegments, BOARD_SECONDS) : []);
    setFocusColumnPercents(normalizeFocusColumnPercents(memory.focusColumnPercents));
    setOpenVideoOptionsIndexes(new Set(
      Array.isArray(memory.openVideoOptionsIndexes)
        ? memory.openVideoOptionsIndexes.map(Number).filter(Boolean)
        : memory.openVideoOptionsIndex
          ? [Number(memory.openVideoOptionsIndex)]
          : []
    ));
    Promise.all([
      loadStoryboardBoardState(projectId, episodeId).catch(() => null),
      loadStoryboardBoardVideos(projectId, episodeId).catch(() => null),
    ]).then(([state, videoData]) => {
      if (cancelled || scopeKeyRef.current !== requestScopeKey) return;
      const serverState = state && typeof state === "object" ? state : {};
      const stateRefKeys = Array.isArray(serverState.selectedRefKeys) ? serverState.selectedRefKeys : [...memoryRefKeys];
      const nextRefKeys = new Set(stateRefKeys);
      selectedRefKeysRef.current = nextRefKeys;
      setSelectedRefKeys(nextRefKeys);
      setMessages(Array.isArray(serverState.messages) ? serverState.messages : (Array.isArray(memory.messages) ? memory.messages : []));
      setPendingDirectorAction(serverState.pendingDirectorAction || memory.pendingDirectorAction || null);
      setAspectRatio(firstText(serverState.aspectRatio, memory.aspectRatio, "16:9"));
      setGlobalVideoResolution(firstText(serverState.globalVideoResolution, memory.globalVideoResolution, GUA_VIDEO_RESOLUTION));
      setOfficialDomesticModel(getDomesticSeedanceModel(serverState.officialDomesticModel || memory.officialDomesticModel)?.id || DOMESTIC_SEEDANCE_MODELS[0].id);
      setVideoProviderOverride(isDomesticOfficialSeedance(serverState.videoProviderOverride || memory.videoProviderOverride) ? VIDEO_PROVIDER_VOLCENGINE_SEEDANCE : normalizeSeedance25SubmissionProvider(
        serverState.videoProviderOverride || memory.videoProviderOverride,
      ));
      setSegmentationMode(serverState.segmentationMode === "manual" || memory.segmentationMode === "manual" ? "manual" : "ai");
      setPresentationModeState(normalizePresentationMode(serverState.presentationMode || memory.presentationMode));
      const restoredManualSegments = Array.isArray(serverState.manualSegments)
        ? serverState.manualSegments
        : Array.isArray(memory.manualSegments)
          ? memory.manualSegments
          : [];
      setManualSegments(normalizeManualSegments(restoredManualSegments, BOARD_SECONDS));
      setFocusColumnPercents(normalizeFocusColumnPercents(serverState.focusColumnPercents || memory.focusColumnPercents));
      const persistedVideos = mapPersistedVideos(videoData?.videos);
      setBoardVideos(mergeBoardVideoMaps(
        memory.boardVideos,
        serverState.boardVideos,
        persistedVideos
      ));
      setBoardImageHistories(mergeBoardImageHistoryMaps(
        memory.boardImageHistories,
        serverState.boardImageHistories
      ));
      setVideoDrafts(
        serverState.videoDrafts && typeof serverState.videoDrafts === "object"
          ? serverState.videoDrafts
          : (memory.videoDrafts && typeof memory.videoDrafts === "object" ? memory.videoDrafts : {})
      );
      const restoredActiveTask = serverState.activeBoardGenerationTask && typeof serverState.activeBoardGenerationTask === "object"
        ? serverState.activeBoardGenerationTask
        : (memory.activeBoardGenerationTask && typeof memory.activeBoardGenerationTask === "object" ? memory.activeBoardGenerationTask : null);
      setActiveBoardGenerationTask(restoredActiveTask);
      if (isStoryboardGenerationTaskActive(restoredActiveTask)) {
        setLoading("generate");
      }
      setOpenVideoOptionsIndexes(new Set(
        Array.isArray(serverState.openVideoOptionsIndexes)
          ? serverState.openVideoOptionsIndexes.map(Number).filter(Boolean)
          : Array.isArray(memory.openVideoOptionsIndexes)
            ? memory.openVideoOptionsIndexes.map(Number).filter(Boolean)
            : []
      ));
    }).finally(() => {
      if (!cancelled && scopeKeyRef.current === requestScopeKey) {
        hydratedScopeRef.current = requestScopeKey;
        setMemoryReady(true);
      }
    });
    return () => { cancelled = true; };
  }, [projectId, episodeId, mapPersistedVideos, scopeKey]);

  useEffect(() => {
    if (!projectId || !episodeId || !memoryReady || hydratedScopeRef.current !== scopeKey) return;
    const targetScopeKey = scopeKey;
    saveMemory(projectId, episodeId, memoryPayload);
    if (saveStateTimerRef.current) {
      clearTimeout(saveStateTimerRef.current);
    }
    saveStateTimerRef.current = setTimeout(() => {
      if (scopeKeyRef.current !== targetScopeKey || hydratedScopeRef.current !== targetScopeKey) return;
      queueStoryboardStateSave(projectId, episodeId, targetScopeKey, memoryPayload).catch(() => {
        // 后端记忆失败不阻断当前制作，仍有本地兜底。
      });
    }, 600);
  }, [projectId, episodeId, memoryPayload, memoryReady, queueStoryboardStateSave, scopeKey]);

  useEffect(() => () => {
    if (saveStateTimerRef.current) {
      clearTimeout(saveStateTimerRef.current);
    }
    boardGenerationPollingRef.current?.stop?.();
    boardGenerationPollingRef.current = null;
    videoPollingMapRef.current.forEach((stopPolling) => stopPolling?.());
    videoPollingMapRef.current.clear();
    stoppedVideoTaskIdsRef.current.clear();
    stoppedVideoBoardIndexesRef.current.clear();
  }, []);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [messages.length, loading]);

  const loadAssets = useCallback(async () => {
    if (!projectId) return { characters: [], scenes: [], props: [] };
    const requestScopeKey = scopeKey;
    const requestId = ++assetLoadRequestRef.current;
    setAssetsLoading(true);
    try {
      const [characters, scenes, props] = await Promise.all([
        authenticatedFetch(`${API_HOST}/api/characters?projectId=${projectId}&summary=true`).catch(() => []),
        authenticatedFetch(`${API_HOST}/api/scenes?projectId=${projectId}&summary=true`).catch(() => []),
        authenticatedFetch(`${API_HOST}/api/props?projectId=${projectId}&summary=true`).catch(() => []),
      ]);
      const nextAssets = {
        characters: Array.isArray(characters) ? characters : [],
        scenes: Array.isArray(scenes) ? scenes : [],
        props: Array.isArray(props) ? props : [],
      };
      if (scopeKeyRef.current !== requestScopeKey || requestId !== assetLoadRequestRef.current) return nextAssets;
      setAssets(nextAssets);
      return nextAssets;
    } finally {
      if (scopeKeyRef.current === requestScopeKey && requestId === assetLoadRequestRef.current) setAssetsLoading(false);
    }
  }, [projectId, scopeKey]);

  const getAssetsForGeneration = useCallback(async () => {
    if (assetsLoading || (!assets.characters.length && !assets.scenes.length && !assets.props.length)) {
      return loadAssets();
    }
    return assets;
  }, [assets, assetsLoading, loadAssets]);

  const loadData = useCallback(async () => {
    if (!projectId || !episodeId) return;
    const requestScopeKey = scopeKey;
    const requestId = ++dataLoadRequestRef.current;
    setLoading("load");
    setError("");
    try {
      const [episodeList, savedBoards, projectList] = await Promise.all([
        authenticatedFetch(`${API_HOST}/api/project/episode/list?projectId=${projectId}`),
        authenticatedFetch(`${API_HOST}/api/episode-storyboard-board/list?projectId=${projectId}&episodeId=${episodeId}`).catch(() => ({ boards: [] })),
        authenticatedFetch(`${API_HOST}/api/project/list`).catch(() => []),
      ]);
      const currentEpisode = (Array.isArray(episodeList) ? episodeList : []).find((item) => String(item.id) === String(episodeId));
      const currentProject = (Array.isArray(projectList) ? projectList : []).find((item) => String(item.id) === String(projectId));
      if (scopeKeyRef.current !== requestScopeKey || requestId !== dataLoadRequestRef.current) return;
      if (currentProject) {
        try {
          window.localStorage.setItem(`project_runtime_profile_${projectId}`, JSON.stringify(currentProject));
          window.dispatchEvent(new CustomEvent("project-runtime-profile-change", {
            detail: { projectId, project: currentProject },
          }));
        } catch {
          // ignore profile cache failures
        }
      }
      setEpisode(currentEpisode || null);
      setProjectTitle(firstText(currentProject?.name, currentProject?.title, currentEpisode?.projectName, currentEpisode?.projectTitle, ""));
      setProjectStylePrompt(firstText(currentProject?.stylePrompt, currentProject?.style, ""));
      setBoards(Array.isArray(savedBoards?.boards) ? savedBoards.boards : []);
      void loadAssets();
      if (!currentEpisode) {
        setError("没有找到当前集，请先在剧本专区导入总剧本并按集拆分。");
      }
    } catch (err) {
      if (scopeKeyRef.current !== requestScopeKey || requestId !== dataLoadRequestRef.current) return;
      setError(err.message || "加载故事板导演台失败");
      toast.error(err.message || "加载故事板导演台失败", 0);
    } finally {
      if (scopeKeyRef.current === requestScopeKey && requestId === dataLoadRequestRef.current) setLoading("");
    }
  }, [episodeId, loadAssets, projectId, scopeKey, toast]);

  const captureBoardImageHistory = useCallback((boardIndex, entries = []) => {
    const index = Number(boardIndex);
    if (!index) return;
    const normalizedEntries = entries
      .map((entry) => buildBoardImageHistoryEntry({ ...(entry || {}), index }))
      .filter(Boolean);
    if (!normalizedEntries.length) return;
    setBoardImageHistories((current) => ({
      ...(current || {}),
      [index]: mergeHistoryEntries(current?.[index], normalizedEntries, (item) => normalizeImageUrl(item?.imageUrl || item?.url)),
    }));
  }, []);

  const mergeBoardWithHistory = useCallback((incoming) => {
    const boardIndex = Number(incoming?.index);
    setBoards((current) => {
      const previous = current.find((item) => Number(item?.index) === boardIndex);
      captureBoardImageHistory(boardIndex, [
        previous ? { ...previous, historyLabel: "上一版整图" } : null,
        incoming ? { ...incoming, historyLabel: "最新整图" } : null,
      ]);
      return mergeBoardIntoList(current, incoming);
    });
  }, [captureBoardImageHistory]);

  const refreshPersistedBoards = useCallback(async (options = {}) => {
    if (!projectId || !episodeId) return [];
    const requestScopeKey = scopeKey;
    const savedBoards = await authenticatedFetch(`${API_HOST}/api/episode-storyboard-board/list?projectId=${projectId}&episodeId=${episodeId}`).catch(() => ({ boards: [] }));
    const list = Array.isArray(savedBoards?.boards) ? savedBoards.boards : [];
    if (scopeKeyRef.current !== requestScopeKey) return [];
    const freshAfter = Number(options?.freshAfter) || 0;
    const visibleList = freshAfter > 0
      ? list.filter((board) => {
        const updatedAt = Date.parse(board?.updatedAt || board?.createdAt || "");
        return updatedAt && updatedAt >= freshAfter;
      })
      : list;
    if (visibleList.length) {
      visibleList.forEach((board) => {
        captureBoardImageHistory(Number(board?.index), [{ ...board, historyLabel: "已保存整图" }]);
      });
      setBoards((current) => visibleList.reduce(
        (merged, board) => mergeBoardIntoList(merged, board),
        ensureStoryboardBoardSlots(current, visibleList.length, BOARD_SECONDS)
      ));
    }
    return list;
  }, [captureBoardImageHistory, episodeId, projectId, scopeKey]);

  useEffect(() => {
    if (!memoryReady) return;
    if (!isStoryboardGenerationTaskActive(activeBoardGenerationTask)) return;
    if (loading !== "generate") setLoading("generate");
  }, [activeBoardGenerationTask, loading, memoryReady]);

  useEffect(() => {
    if (!memoryReady) return;
    const task = activeBoardGenerationTaskRef.current;
    if (!isStoryboardGenerationTaskActive(task)) return;
    const expectedBoardCount = Number(task.count) || 0;
    if (expectedBoardCount <= 0) return;
    const secondsPerBoard = Number(task.durationSecondsPerBoard) || BOARD_SECONDS;
    const hasAllSlots = boards.length >= expectedBoardCount
      && Array.from({ length: expectedBoardCount }, (_, index) => index + 1)
        .every((index) => boards.some((board) => Number(board?.index) === index));
    if (hasAllSlots) return;
    setBoards((current) => ensureStoryboardBoardSlots(current, expectedBoardCount, secondsPerBoard));
  }, [
    activeBoardGenerationTask?.count,
    activeBoardGenerationTask?.durationSecondsPerBoard,
    activeBoardGenerationTask?.status,
    activeBoardGenerationTask?.taskId,
    boards,
    memoryReady,
  ]);

  useEffect(() => {
    if (!memoryReady || !projectId || !episodeId) return undefined;
    const task = activeBoardGenerationTaskRef.current;
    if (!isStoryboardGenerationTaskActive(task)) return undefined;
    if (boardGenerationPollingRef.current?.taskId === task.taskId) return undefined;

    const requestScopeKey = scopeKey;
    const controller = new AbortController();
    let taskTerminal = false;
    let retryTimer = null;
    const expectedBoardCount = Number(task.count) || 0;
    const secondsPerBoard = Number(task.durationSecondsPerBoard) || BOARD_SECONDS;
    setLoading("generate");
    if (expectedBoardCount > 0) {
      setBoards((current) => ensureStoryboardBoardSlots(current, expectedBoardCount, secondsPerBoard));
    }

    const recoveryTimer = window.setInterval(() => {
      refreshPersistedBoards().catch(() => {});
    }, 12000);
    const stop = () => {
      window.clearInterval(recoveryTimer);
      controller.abort();
    };
    boardGenerationPollingRef.current = { taskId: task.taskId, stop };

    pollStoryboardBoardTask(task.taskId, {
      start: (info) => {
        if (scopeKeyRef.current !== requestScopeKey) return;
        const count = Number(info?.count) || expectedBoardCount;
        if (count > 0) {
          setBoards((current) => ensureStoryboardBoardSlots(current, count, info?.durationSecondsPerBoard || secondsPerBoard));
        }
      },
      board: (board) => {
        if (scopeKeyRef.current !== requestScopeKey) return;
        mergeBoardWithHistory(board);
      },
      done: (summary) => {
        if (scopeKeyRef.current !== requestScopeKey) return;
        taskTerminal = true;
        const successCount = Number(summary?.successCount) || 0;
        const failedCount = Number(summary?.failedCount) || 0;
        const totalCount = Number(summary?.count) || successCount + failedCount || expectedBoardCount;
        const duration = Number(summary?.durationSecondsPerBoard) || secondsPerBoard;
        setMessages((current) => {
          const alreadyNotified = current.some((message) => message?.id === `a-resume-${task.taskId}`);
          if (alreadyNotified) return current;
          return [...current, {
            id: `a-resume-${task.taskId}`,
            role: "assistant",
            content: `本轮故事板已完成：成功 ${successCount} 张，失败 ${failedCount} 张。每张控制 ${duration} 秒，本轮共 ${totalCount} 张，视频目标约 ${totalCount * duration} 秒。`,
            createdAt: new Date().toISOString(),
          }].slice(-40);
        });
        toast.success(`已恢复并完成 ${successCount} 张连续故事板图`);
      },
    }, {
      signal: controller.signal,
      timeoutMs: task.timeoutMs || 60 * 60 * 1000,
      intervalMs: 5000,
      onProgress: (status) => {
        if (scopeKeyRef.current !== requestScopeKey) return;
        setActiveBoardGenerationTask((current) => (
          current?.taskId === task.taskId
            ? {
              ...current,
              status: status?.status || current.status || "processing",
              progress: status?.progress,
              currentStage: status?.currentStage,
              currentStageText: status?.currentStageText,
              updatedAt: new Date().toISOString(),
            }
            : current
        ));
      },
    }).catch((err) => {
      if (err?.name === "AbortError") return;
      if (scopeKeyRef.current !== requestScopeKey) return;
      const message = err.message || "连续故事板生成失败";
      if (err?.recoverable) {
        setActiveBoardGenerationTask((current) => (
          current?.taskId === task.taskId
            ? {
              ...current,
              status: "processing",
              recoveryError: message,
              updatedAt: new Date().toISOString(),
            }
            : current
        ));
        toast.warning("故事板任务仍在后台执行，正在重新连接进度");
        return;
      }
      taskTerminal = true;
      setBoards((current) => markUnfinishedStoryboardBoardsFailed(current, expectedBoardCount, secondsPerBoard, message));
      setMessages((current) => [...current, {
        id: `e-resume-${task.taskId}-${Date.now()}`,
        role: "assistant",
        content: `恢复故事板任务失败：${message}`,
        createdAt: new Date().toISOString(),
      }].slice(-40));
      toast.error(message, 0);
    }).finally(() => {
      window.clearInterval(recoveryTimer);
      if (scopeKeyRef.current !== requestScopeKey) return;
      refreshPersistedBoards().catch(() => {});
      const ownsCurrentPolling = boardGenerationPollingRef.current?.taskId === task.taskId;
      if (ownsCurrentPolling) {
        boardGenerationPollingRef.current = null;
      }
      if (taskTerminal) {
        setActiveBoardGenerationTask((current) => (
          current?.taskId === task.taskId ? null : current
        ));
      } else if (!controller.signal.aborted && ownsCurrentPolling) {
        retryTimer = window.setTimeout(() => {
          if (scopeKeyRef.current === requestScopeKey) {
            setBoardGenerationResumeNonce((value) => value + 1);
          }
        }, 5000);
      }
      if (ownsCurrentPolling && taskTerminal) {
        setLoading((current) => (current === "generate" ? "" : current));
      }
    });

    return () => {
      if (boardGenerationPollingRef.current?.taskId === task.taskId) {
        boardGenerationPollingRef.current = null;
      }
      if (retryTimer) window.clearTimeout(retryTimer);
      stop();
    };
  }, [activeBoardGenerationTask?.taskId, boardGenerationResumeNonce, episodeId, memoryReady, mergeBoardWithHistory, projectId, refreshPersistedBoards, scopeKey, toast]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (segmentationMode !== "manual") return;
    if (!scriptText.trim()) {
      setManualSegments([]);
      return;
    }
    setManualSegments((current) => (
      current?.length ? current : buildManualStoryboardSegments(scriptText, BOARD_COUNT, BOARD_SECONDS, presentationMode)
    ));
  }, [scriptText, segmentationMode, presentationMode]);

  const selectMatchedAssets = () => {
    const source = `${episode?.title || ""}\n${scriptText}`;
    const next = new Set(selectedRefKeysRef.current);
    let added = 0;
    Object.entries(assets).forEach(([type, list]) => {
      (list || []).forEach((asset) => {
        if (!getAssetImageUrl(asset) || !assetMatchesText(asset, source)) return;
        const key = getAssetKey(type, asset);
        if (!next.has(key) && next.size < referenceLimit) {
          next.add(key);
          added += 1;
        }
      });
    });
    selectedRefKeysRef.current = next;
    setSelectedRefKeys(next);
    if (added > 0) toast.success(`已按本集剧本匹配 ${added} 张参考图`);
    else toast.info("没有匹配到新的参考图，可以手动选择");
  };

  const clearRefs = () => {
    const next = new Set();
    selectedRefKeysRef.current = next;
    setSelectedRefKeys(next);
  };

  const markBoardRegenerating = useCallback((boardIndex, active) => {
    setRegeneratingBoardIndexes((current) => {
      const next = new Set(current);
      if (active) next.add(Number(boardIndex));
      else next.delete(Number(boardIndex));
      return next;
    });
  }, []);

  const markBoardVideoSubmitting = useCallback((boardIndex, active) => {
    setGeneratingVideoBoardIndexes((current) => {
      const next = new Set(current);
      if (active) next.add(Number(boardIndex));
      else next.delete(Number(boardIndex));
      return next;
    });
  }, []);

  const removeSelectedRef = (key) => {
    const next = new Set(selectedRefKeysRef.current);
    next.delete(key);
    selectedRefKeysRef.current = next;
    setSelectedRefKeys(next);
  };

  const toggleAsset = (type, asset) => {
    const key = getAssetKey(type, asset);
    const next = new Set(selectedRefKeysRef.current);
    if (next.has(key)) {
      next.delete(key);
      selectedRefKeysRef.current = next;
      setSelectedRefKeys(next);
      return;
    }
    if (!getAssetImageUrl(asset)) {
      toast.warning("这个资产还没有可用参考图");
      return;
    }
    if (next.size >= referenceLimit) {
      toast.warning(`参考图最多 ${referenceLimit} 张，请先取消一些`);
      return;
    }
    next.add(key);
    selectedRefKeysRef.current = next;
    setSelectedRefKeys(next);
  };

  const openVideoReferencePicker = (boardIndex, currentItems = []) => {
    const normalizedBoardIndex = Number(boardIndex);
    if (!normalizedBoardIndex) return;
    updateVideoDraft(normalizedBoardIndex, {
      inputPackage: normalizeVideoPackageItems(currentItems, videoReferenceLimit),
    });
    setVideoReferencePickerBoardIndex(normalizedBoardIndex);
  };

  const videoReferencePickerSelectedKeys = useMemo(() => {
    const boardIndex = Number(videoReferencePickerBoardIndex);
    if (!boardIndex) return new Set();
    const packageItems = normalizeVideoPackageItems(
      videoDrafts?.[boardIndex]?.inputPackage,
      videoReferenceLimit,
    );
    const selectedUrls = new Set(
      packageItems
        .filter((item) => !["current_board", "previous_board"].includes(item.role))
        .map((item) => normalizeImageUrl(item.url))
        .filter(Boolean),
    );
    const keys = new Set();
    Object.entries(assets).forEach(([type, list]) => {
      (list || []).forEach((asset) => {
        if (selectedUrls.has(normalizeImageUrl(getAssetImageUrl(asset)))) {
          keys.add(getAssetKey(type, asset));
        }
      });
    });
    return keys;
  }, [assets, videoDrafts, videoReferenceLimit, videoReferencePickerBoardIndex]);

  const videoReferencePickerAssetLimit = useMemo(() => {
    const boardIndex = Number(videoReferencePickerBoardIndex);
    if (!boardIndex) return videoReferenceLimit;
    const reservedCount = normalizeVideoPackageItems(
      videoDrafts?.[boardIndex]?.inputPackage,
      videoReferenceLimit,
    ).filter((item) => ["current_board", "previous_board"].includes(item.role)).length;
    return Math.max(0, videoReferenceLimit - reservedCount);
  }, [videoDrafts, videoReferenceLimit, videoReferencePickerBoardIndex]);

  const toggleVideoReferenceAsset = (type, asset) => {
    const boardIndex = Number(videoReferencePickerBoardIndex);
    if (!boardIndex) return;
    const key = getAssetKey(type, asset);
    const item = buildAssetVideoPackageItems(
      buildSelectedRefsFromKeys(assets, new Set([key])),
      videoReferenceLimit,
    )[0];
    if (!item?.url) {
      toast.warning("这个资产还没有可用参考图");
      return;
    }
    const currentItems = normalizeVideoPackageItems(
      videoDrafts?.[boardIndex]?.inputPackage,
      videoReferenceLimit,
    );
    const existingIndex = currentItems.findIndex((candidate) => (
      !["current_board", "previous_board"].includes(candidate.role)
      && normalizeImageUrl(candidate.url) === normalizeImageUrl(item.url)
    ));
    if (existingIndex < 0 && currentItems.length >= videoReferenceLimit) {
      toast.warning(`${videoProviderLabel} 最多支持 ${videoReferenceLimit} 张视频参考图`);
      return;
    }
    const nextItems = existingIndex >= 0
      ? currentItems.filter((_, index) => index !== existingIndex)
      : [...currentItems, item];
    updateVideoDraft(boardIndex, {
      inputPackage: normalizeVideoPackageItems(nextItems, videoReferenceLimit),
    });
  };

  const uploadVideoReferenceFiles = async (fileList) => {
    const boardIndex = Number(videoReferencePickerBoardIndex);
    const requestScopeKey = scopeKey;
    if (!boardIndex || !supportsSeedance25MediaReferences(videoProvider) || uploadingVideoReferences) return;
    const files = Array.from(fileList || []);
    const items = normalizeVideoPackageItems(videoDrafts?.[boardIndex]?.inputPackage, videoReferenceLimit);
    if (files.some((file) => !/^(image|video|audio)\//.test(file.type))) {
      toast.warning("请选择图片、视频或音频文件");
      return;
    }
    if (files.length + items.length > videoReferenceLimit) {
      toast.warning(`当前最多还能添加 ${Math.max(0, videoReferenceLimit - items.length)} 个参考素材`);
      return;
    }
    setUploadingVideoReferences(true);
    try {
      const uploaded = await Promise.all(files.map(async (file) => {
        const url = await uploadFileToCos(file);
        const mediaType = getSeedance25ReferenceMediaType({ type: file.type, url });
        return { url, name: file.name, type: mediaType, mediaType, role: `reference_${mediaType}` };
      }));
      if (scopeKeyRef.current !== requestScopeKey) return;
      updateVideoDraft(boardIndex, { inputPackage: [...items, ...uploaded] });
      toast.success(`已添加 ${uploaded.length} 个参考素材，生成时使用原始素材 URL`);
    } catch (error) {
      if (scopeKeyRef.current === requestScopeKey) toast.error(error.message || "参考素材上传失败");
    } finally {
      setUploadingVideoReferences(false);
    }
  };

  const clearVideoReferenceAssets = () => {
    const boardIndex = Number(videoReferencePickerBoardIndex);
    if (!boardIndex) return;
    const retained = normalizeVideoPackageItems(
      videoDrafts?.[boardIndex]?.inputPackage,
      videoReferenceLimit,
    ).filter((item) => ["current_board", "previous_board"].includes(item.role));
    updateVideoDraft(boardIndex, { inputPackage: retained });
  };

  const updateBoardVideo = useCallback((boardIndex, patch, options = {}) => {
    const index = Number(boardIndex);
    if (!index) return;
    setBoardVideos((current) => {
      const previous = current?.[index] || {};
      const previousHistory = Array.isArray(previous.history) ? previous.history : [];
      let nextHistory = previousHistory;
      if (options.captureCurrent) {
        nextHistory = mergeHistoryEntries(
          nextHistory,
          [buildVideoHistoryEntry(previous, index, options.currentLabel || "切换前版本")],
          (item) => firstText(item?.videoUrl, item?.url)
        );
      }
      const nextVideo = {
        ...previous,
        ...patch,
      };
      const nextVideoUrl = getBoardVideoUrl(nextVideo);
      if ((nextVideo.status === "success" || patch?.status === "success") && nextVideoUrl) {
        nextHistory = mergeHistoryEntries(
          nextHistory,
          [buildVideoHistoryEntry(nextVideo, index, options.nextLabel || "当前版本")],
          (item) => firstText(item?.videoUrl, item?.url)
        );
      }
      return {
        ...current,
        [index]: {
          ...nextVideo,
          history: nextHistory,
        },
      };
    });
  }, []);

  const syncBoardVideoRecord = useCallback((boardIndex, patch) => {
    if (!projectId || !episodeId || !boardIndex || !firstText(patch?.videoUrl, patch?.url)) return;
    updateStoryboardBoardVideo(projectId, episodeId, boardIndex, patch).catch((error) => {
      console.warn("[EpisodeStoryboardBoard] sync video record failed", error);
    });
  }, [episodeId, projectId]);

  const updateVideoDraft = useCallback((boardIndex, patch) => {
    setVideoDrafts((current) => ({
      ...current,
      [boardIndex]: {
        ...(current?.[boardIndex] || {}),
        ...patch,
      },
    }));
  }, []);

  const selectBoardImageHistory = useCallback((board, entry) => {
    const boardIndex = Number(board?.index || entry?.boardIndex);
    const imageUrl = normalizeImageUrl(entry?.imageUrl || entry?.url);
    if (!boardIndex || !imageUrl) return;
    const nextBoard = {
      ...board,
      imageUrl,
      status: "success",
      prompt: firstText(entry?.prompt, board?.prompt),
      title: firstText(entry?.title, board?.title, `第${boardIndex}张故事板`),
      segmentText: firstText(entry?.segmentText, board?.segmentText),
      timeRange: firstText(entry?.timeRange, board?.timeRange),
      durationSeconds: entry?.durationSeconds ?? board?.durationSeconds,
      referenceCounts: entry?.referenceCounts ?? board?.referenceCounts,
      referenceSnapshot: entry?.referenceSnapshot ?? board?.referenceSnapshot,
      openaiGeneration: entry?.openaiGeneration ?? board?.openaiGeneration,
      emotionPlan: entry?.emotionPlan ?? board?.emotionPlan,
      blockingPlan: entry?.blockingPlan ?? board?.blockingPlan,
      presentationMode: firstText(entry?.presentationMode, board?.presentationMode),
      selectedImageHistoryId: entry?.id,
      updatedAt: new Date().toISOString(),
    };
    captureBoardImageHistory(boardIndex, [
      { ...board, historyLabel: "切换前整图" },
      { ...nextBoard, historyLabel: "当前整图" },
    ]);
    setBoards((current) => mergeBoardIntoList(current, nextBoard));
    setVideoDrafts((current) => {
      const draft = current?.[boardIndex];
      if (!draft || !Array.isArray(draft.inputPackage)) return current;
      const inputPackage = normalizeVideoPackageItems(draft.inputPackage.map((item, index) => {
        if (item?.role === "current_board" || item?.id === `board:${boardIndex}` || index === 0) {
          return {
            ...item,
            id: `board:${boardIndex}`,
            role: "current_board",
            type: "当前故事板",
            name: `当前故事板 ${boardIndex}`,
            url: imageUrl,
            note: "视频主参考",
          };
        }
        return item;
      }), videoReferenceLimit);
      return {
        ...current,
        [boardIndex]: {
          ...draft,
          inputPackage,
          prompt: "",
        },
      };
    });
    updateStoryboardBoard(projectId, episodeId, boardIndex, nextBoard).catch((error) => {
      console.warn("[EpisodeStoryboardBoard] persist selected board image failed", error);
    });
  }, [captureBoardImageHistory, episodeId, projectId, videoReferenceLimit]);

  const selectBoardVideoHistory = useCallback((board, entry) => {
    const boardIndex = Number(board?.index || entry?.boardIndex);
    const videoUrl = firstText(entry?.videoUrl, entry?.url);
    if (!boardIndex || !videoUrl) return;
    const { history: _history, ...entryData } = entry || {};
    const nextVideo = {
      ...entryData,
      status: "success",
      progress: 100,
      videoUrl,
      url: videoUrl,
      selectedHistoryId: entry?.id,
      updatedAt: new Date().toISOString(),
    };
    updateBoardVideo(boardIndex, nextVideo, {
      captureCurrent: true,
      currentLabel: "切换前视频",
      nextLabel: firstText(entry?.label, "切换版本"),
    });
    syncBoardVideoRecord(boardIndex, nextVideo);
  }, [syncBoardVideoRecord, updateBoardVideo]);

  const applyGlobalVideoResolution = useCallback((resolution) => {
    const nextResolution = normalizeVideoResolutionForProvider(
      videoProvider,
      firstText(resolution, GUA_VIDEO_RESOLUTION),
      GUA_VIDEO_RESOLUTION,
    );
    setGlobalVideoResolution(nextResolution);
    setVideoDrafts((current) => {
      const next = { ...(current || {}) };
      const boardIndexes = boards
        .map((board) => Number(board?.index))
        .filter(Boolean);
      boardIndexes.forEach((boardIndex) => {
        next[boardIndex] = {
          ...(next?.[boardIndex] || {}),
          resolution: nextResolution,
        };
      });
      Object.keys(next).forEach((key) => {
        next[key] = {
          ...(next?.[key] || {}),
          resolution: nextResolution,
        };
      });
      return next;
    });
  }, [boards, videoProvider]);

  const changeVideoProviderOverride = useCallback((value) => {
    const nextOverride = isDomesticOfficialSeedance(value) ? VIDEO_PROVIDER_VOLCENGINE_SEEDANCE : normalizeSeedance25SubmissionProvider(value);
    setVideoProviderOverride(nextOverride);
    const targetProvider = nextOverride || projectRuntimeVideoProvider;
    const capabilities = getSeedance25Capabilities(targetProvider);
    if (!capabilities) {
      setVideoDrafts((current) => {
        const next = {};
        Object.entries(current || {}).forEach(([key, draft]) => {
          next[key] = {
            ...(draft || {}),
            duration: normalizeVideoDuration(draft?.duration ?? BOARD_SECONDS),
          };
        });
        return next;
      });
      return;
    }
    setGlobalVideoResolution(capabilities.resolutions[0]);
    setVideoDrafts((current) => {
      const next = {};
      Object.entries(current || {}).forEach(([key, draft]) => {
        next[key] = {
          ...(draft || {}),
          duration: normalizeSeedance25Duration(targetProvider, draft?.duration ?? BOARD_SECONDS),
          ratio: normalizeSeedance25Ratio(targetProvider, draft?.ratio, projectDefaultVideoRatio),
          resolution: capabilities.resolutions[0],
        };
      });
      return next;
    });
  }, [projectDefaultVideoRatio, projectRuntimeVideoProvider]);

  useEffect(() => {
    if (!videoResolutionOptions.includes(globalVideoResolution)) {
      setGlobalVideoResolution(videoResolutionOptions[0]);
    }
  }, [globalVideoResolution, videoResolutionOptions]);

  useEffect(() => {
    if (!memoryReady) return;
    const requestScopeKey = scopeKey;
    Object.entries(boardVideos || {}).forEach(([rawBoardIndex, video]) => {
      const boardIndex = Number(rawBoardIndex);
      if (!boardIndex || !VIDEO_BUSY_STATUSES.has(video?.status)) return;
      const taskId = video?.taskId;
      if (!taskId) {
        const createdAt = video?.createdAt ? Date.parse(video.createdAt) : 0;
        if (createdAt && Date.now() - createdAt < 120000) return;
        updateBoardVideo(boardIndex, {
          status: "failed",
          error: "视频提交状态已丢失，请重新提交本张视频。",
          completedAt: new Date().toISOString(),
        });
        return;
      }
      if (
        videoPollingMapRef.current.has(taskId)
        || stoppedVideoTaskIdsRef.current.has(taskId)
        || stoppedVideoBoardIndexesRef.current.has(boardIndex)
      ) return;

      const resolvedProvider = video?.provider || resolveSeedance25Provider({ model: video?.model }) || videoProvider;
      const resolvedModel = video?.model || projectVideoModel;
      const stopPolling = startPolling(resolvedProvider, taskId, {
        projectId: projectId ? Number(projectId) : undefined,
        episodeId: episodeId ? Number(episodeId) : undefined,
        storyboardBoardIndex: boardIndex,
        prompt: video?.prompt,
        model: resolvedModel,
        resolution: video?.resolution,
        onSuccess: (data) => {
          if (scopeKeyRef.current !== requestScopeKey) return;
          if (stoppedVideoTaskIdsRef.current.has(taskId) || stoppedVideoBoardIndexesRef.current.has(boardIndex)) return;
          videoPollingMapRef.current.delete(taskId);
          updateBoardVideo(boardIndex, {
            ...data,
            status: "success",
            progress: 100,
            videoUrl: data?.videoUrl || data?.url || data?.result?.videoUrl,
            completedAt: new Date().toISOString(),
          }, { nextLabel: "生成完成" });
          toast.success(`第${boardIndex}张视频生成完成`);
        },
        onError: (errorMsg) => {
          if (scopeKeyRef.current !== requestScopeKey) return;
          if (stoppedVideoTaskIdsRef.current.has(taskId) || stoppedVideoBoardIndexesRef.current.has(boardIndex)) return;
          videoPollingMapRef.current.delete(taskId);
          updateBoardVideo(boardIndex, {
            status: "failed",
            error: errorMsg || "视频生成失败",
            completedAt: new Date().toISOString(),
          });
        },
        onProgress: ({ progress, status }) => {
          if (scopeKeyRef.current !== requestScopeKey) return;
          if (stoppedVideoTaskIdsRef.current.has(taskId) || stoppedVideoBoardIndexesRef.current.has(boardIndex)) return;
          updateBoardVideo(boardIndex, {
            status: status || "processing",
            progress,
          });
        },
      });
      videoPollingMapRef.current.set(taskId, stopPolling);
    });
  }, [boardVideos, episodeId, memoryReady, projectId, projectVideoModel, scopeKey, toast, updateBoardVideo, videoProvider]);

  const syncGuaMetaToDrafts = useCallback((url, meta) => {
    const normalizedUrl = normalizeImageUrl(url);
    if (!normalizedUrl) return;
    setVideoDrafts((current) => {
      const next = {};
      Object.entries(current || {}).forEach(([boardIndex, draft]) => {
        if (!Array.isArray(draft?.inputPackage)) {
          next[boardIndex] = draft;
          return;
        }
        next[boardIndex] = {
          ...draft,
          inputPackage: applyGuaMetaToPackageItems(draft.inputPackage, normalizedUrl, meta),
        };
      });
      return next;
    });
  }, []);

  const pollStoryboardReferenceAssetStatus = useCallback(async ({ url, assetId }) => {
    const normalizedUrl = normalizeImageUrl(url);
    if (!assetIngestSupported || !projectId || !normalizedUrl || !assetId) return;
    const requestScopeKey = scopeKey;
    try {
      const fields = getVideoAssetFieldNames(videoProvider);
      if (!fields) throw new Error("当前视频厂商的素材字段未配置");
      const result = await pollVideoAssetUntilTerminal({
        provider: videoProvider,
        projectId,
        url: normalizedUrl,
        assetId,
        shouldContinue: () => scopeKeyRef.current === requestScopeKey,
        onStatus: ({ status }) => {
          if (scopeKeyRef.current !== requestScopeKey) return;
          syncGuaMetaToDrafts(normalizedUrl, {
            ...clearVideoAssetBinding({}),
            [fields.idField]: assetId,
            [fields.statusField]: status,
            assetProvider: videoProvider,
          });
        },
      });
      if (scopeKeyRef.current !== requestScopeKey) return;
      await refreshStatusMap?.();
      if (result.status === "Active") {
        toast.success("故事板参考图已入库完成");
      } else {
        toast.error("故事板参考图入库失败");
      }
    } catch (err) {
      if (scopeKeyRef.current !== requestScopeKey) return;
      // 超时仅停止前端等待，保留 Processing 等待后续刷新。
      toast.warning(err.message || "查询入库状态失败");
    }
  }, [assetIngestSupported, projectId, refreshStatusMap, scopeKey, syncGuaMetaToDrafts, toast, videoProvider]);

  const uploadPackageItemToGuaAsset = useCallback(async (item, options = {}) => {
    const silent = options?.silent === true;
    const normalizedUrl = normalizeImageUrl(item?.url);
    if (!projectId || !normalizedUrl) {
      if (!silent) toast.warning("没有可入库的参考图");
      return { ok: false, skipped: true };
    }
    if (!assetIngestSupported || !videoAssetApiBase) {
      if (!silent) toast.error("当前项目的视频服务不支持素材入库");
      return { ok: false, skipped: true };
    }
    const requestScopeKey = scopeKey;
    if (scopeKeyRef.current !== requestScopeKey) return { ok: false, stale: true };
    const existingMeta = getAssetMetaForUrl(assetMetaMap, normalizedUrl);
    const existingEffective = getEffectiveAssetMeta(existingMeta, videoProvider);
    const existingAssetId = existingEffective.assetId;
    const existingStatus = existingEffective.status;
    if (existingAssetId && existingStatus === "Active") {
      syncGuaMetaToDrafts(normalizedUrl, existingMeta);
      if (!silent) toast.success("这张图已经入库");
      return { ok: true, reused: true, active: true };
    }

    setUploadingAssetUrls((current) => new Set(current).add(normalizedUrl));
    try {
      const result = await authenticatedFetch(`${API_HOST}${videoAssetApiBase}/create`, {
        method: "POST",
        body: JSON.stringify({
          projectId: Number(projectId),
          episodeId: episodeId ? Number(episodeId) : undefined,
          shotNumber: item?.boardIndex ? Number(item.boardIndex) : undefined,
          url: normalizedUrl,
          name: firstText(item?.name, item?.type, "故事板参考图"),
          assetType: "Image",
          typeCode: "storyboard_image",
          source: "storyboard",
          sourceDetail: "episode_storyboard_board",
        }),
      });
      if (scopeKeyRef.current !== requestScopeKey) return { ok: false, stale: true };
      const rawResultProvider = firstText(result?.provider, result?.assetProvider);
      if (rawResultProvider && !isSameVideoAssetLibrary(rawResultProvider, videoProvider)) {
        throw new Error(`素材平台返回不一致：期望 ${videoProviderLabel}，实际 ${rawResultProvider}`);
      }
      const applied = applyAssetCreateResult({}, result, videoProvider);
      if (!applied.accepted || applied.provider !== videoProvider) throw new Error("参考图入库返回格式不正确");
      const effective = getEffectiveAssetMeta(applied.target, videoProvider);
      const createdAssetId = effective.assetId;
      const createdStatus = effective.status;
      if (!createdAssetId) throw new Error("参考图入库未返回素材 ID");
      const meta = { ...applied.target, assetProvider: applied.provider };
      syncGuaMetaToDrafts(normalizedUrl, meta);
      await refreshStatusMap?.();
      if (!silent) toast.success(result?.reused ? "已同步入库状态" : "已提交素材入库");
      if (createdAssetId && createdStatus !== "Active") {
        void pollStoryboardReferenceAssetStatus({
          url: normalizedUrl,
          assetId: createdAssetId,
        });
      }
      return { ok: true, reused: !!result?.reused, status: createdStatus };
    } catch (err) {
      if (scopeKeyRef.current !== requestScopeKey) return { ok: false, stale: true };
      if (!silent) toast.error(err.message || "参考图入库失败", 0);
      return { ok: false, error: err };
    } finally {
      if (scopeKeyRef.current === requestScopeKey) {
        setUploadingAssetUrls((current) => {
          const next = new Set(current);
          next.delete(normalizedUrl);
          return next;
        });
      }
    }
  }, [assetIngestSupported, assetMetaMap, episodeId, pollStoryboardReferenceAssetStatus, projectId, refreshStatusMap, scopeKey, syncGuaMetaToDrafts, toast, videoAssetApiBase, videoProvider, videoProviderLabel]);

  const buildBoardAssetItem = useCallback((board) => {
    const boardIndex = Number(board?.index);
    const url = normalizeImageUrl(board?.imageUrl);
    if (!boardIndex || !url) return null;
    return {
      url,
      name: firstText(board?.title, `第${boardIndex}张故事板`),
      type: "故事板",
      role: "storyboard_board",
      boardIndex,
    };
  }, []);

  const uploadBoardAssetHandler = useStableEvent(async (board) => {
    const item = buildBoardAssetItem(board);
    if (!item) {
      toast.warning("这张故事板还没有可入库的图片");
      return;
    }
    await uploadPackageItemToGuaAsset(item);
  });

  const uploadAllBoardAssetsHandler = useStableEvent(async () => {
    const requestScopeKey = scopeKey;
    const items = boards.map(buildBoardAssetItem).filter(Boolean);
    if (!items.length) {
      toast.warning("还没有可入库的故事板图片");
      return;
    }
    setUploadingAllBoardAssets(true);
    let success = 0;
    let failed = 0;
    try {
      for (const item of items) {
        if (scopeKeyRef.current !== requestScopeKey) return;
        const result = await uploadPackageItemToGuaAsset(item, { silent: true });
        if (result?.ok) success += 1;
        else failed += 1;
      }
      if (scopeKeyRef.current !== requestScopeKey) return;
      await refreshStatusMap?.();
      if (scopeKeyRef.current !== requestScopeKey) return;
      if (failed > 0) {
        toast.warning(`故事板入库完成 ${success} 张，失败 ${failed} 张`);
      } else {
        toast.success(`已提交 ${success} 张故事板入库`);
      }
    } finally {
      if (scopeKeyRef.current === requestScopeKey) setUploadingAllBoardAssets(false);
    }
  });

  const generateVideoFromBoard = async (board, options = {}) => {
    const requestScopeKey = scopeKey;
    if (scopeKeyRef.current !== requestScopeKey) return false;
    if (!isSeedance25 && !isSeedanceVideoProvider(videoProvider)) {
      toast.warning("项目视频厂商配置尚未加载，请刷新项目后重试");
      return false;
    }
    const boardIndex = Number(board?.index);
    const boardImageUrl = normalizeImageUrl(board?.imageUrl);
    if (!episode || !boardIndex) {
      toast.warning("请先选择有效故事板");
      return false;
    }
    if (!boardImageUrl) {
      toast.warning("这张故事板还没有图片，不能生成视频");
      return false;
    }
    if (initialGenerating) {
      toast.warning("整集故事板正在生成，请稍等");
      return false;
    }
    if (regeneratingBoardIndexes.has(boardIndex)) {
      toast.warning(`第${boardIndex}张正在重新生图，请稍等`);
      return false;
    }
    if (generatingVideoBoardIndexes.has(boardIndex) || VIDEO_BUSY_STATUSES.has(boardVideos?.[boardIndex]?.status)) {
      toast.warning(`第${boardIndex}张视频正在生成，请稍等`);
      return false;
    }

    const assetGroupsForRequest = await getAssetsForGeneration();
    if (scopeKeyRef.current !== requestScopeKey) return false;
    const latestAssetMaps = isSeedance25 ? null : await refreshStatusMap?.();
    if (scopeKeyRef.current !== requestScopeKey) return false;
    const latestAssetMetaMap = latestAssetMaps?.assetMetaMap || assetMetaMap;
    const latestSelectedRefs = buildSelectedRefsFromKeys(assetGroupsForRequest, selectedRefKeysRef.current);
    const rawDraft = videoDrafts?.[boardIndex] || {};
    if (isSeedance25 && validateSeedance25References(videoProvider, rawDraft.inputPackage).rejected.length) {
      toast.warning('视频输入包缺少可用原始素材 URL，请重新选择原始素材；素材库 ID 不能替代原图');
      return false;
    }
    const draft = ensureVideoDraft(
      board,
      boards,
      latestSelectedRefs,
      { ...rawDraft, resolution: firstText(rawDraft.resolution, globalVideoResolution, GUA_VIDEO_RESOLUTION) },
      aspectRatio,
      latestAssetMetaMap,
      videoReferenceLimit,
    );
    const packageItems = normalizeVideoPackageItems(draft.inputPackage, videoReferenceLimit);
    const requestText = firstText(directorRequest);
    const defaultPrompt = buildBoardVideoPrompt(board, episode, requestText, packageItems, draft.previousSummary, presentationMode, videoReferenceLimit);
    const markedPrompt = firstText(draft.prompt, defaultPrompt);
    const refUrls = packageItems.map((item) => item.url);
    const resolvedDefaultPrompt = resolveRefMarkers(defaultPrompt, refUrls);
    let prompt = firstText(resolveRefMarkers(markedPrompt, refUrls), resolvedDefaultPrompt, defaultPrompt);
    if (draft.noSubtitle) {
      prompt = `${NO_SUBTITLE_PROMPT}${prompt}`;
    }
    if (firstText(draft.lighting)) {
      prompt = applyLighting(prompt, draft.lighting);
    }
    const durationSeconds = isSeedance25
      ? normalizeSeedance25Duration(videoProvider, draft.duration ?? board?.durationSeconds ?? BOARD_SECONDS)
      : normalizeVideoDuration(draft.duration ?? board?.durationSeconds ?? BOARD_SECONDS);
    const draftRatio = firstText(draft.ratio, projectDefaultVideoRatio, DEFAULT_VIDEO_RATIO);
    const seedance25SupportsAdaptive = Boolean(seedance25Capabilities?.ratios?.includes("adaptive"));
    const requestedAspectRatio = draftRatio === "adaptive" && !seedance25SupportsAdaptive
      ? firstText(projectDefaultVideoRatio, DEFAULT_VIDEO_RATIO)
      : draftRatio;
    const payloadAspectRatio = isSeedance25
      ? normalizeSeedance25Ratio(videoProvider, requestedAspectRatio, DEFAULT_VIDEO_RATIO)
      : requestedAspectRatio;
    const resolution = isSeedance25
      ? normalizeSeedance25Resolution(
        videoProvider,
        firstText(draft.resolution, globalVideoResolution),
      )
      : isDomesticOfficialSeedance(videoProvider)
      ? normalizeDomesticSeedanceResolution(projectVideoModel, firstText(draft.resolution, globalVideoResolution))
      : normalizeVideoResolutionForProvider(
        videoProvider,
        firstText(draft.resolution, GUA_VIDEO_RESOLUTION),
        GUA_VIDEO_RESOLUTION,
      );
    const generateAudio = draft.generateAudio ?? true;
    const referenceValidation = isSeedance25
      ? validateSeedance25References(videoProvider, packageItems)
      : { accepted: [], rejected: [] };
    if (isSeedance25 && referenceValidation.rejected.length > 0) {
      toast.warning(`${videoProviderLabel} 的视频输入包包含 ${referenceValidation.rejected.length} 个无效项；请使用当前线路支持的原始素材 URL 或图片数据，不能传其他厂商的素材库 ID`);
      return false;
    }
    if (isSeedance25 && packageItems.filter((item) => getSeedance25ReferenceMediaType(item) === "image").length > seedance25Capabilities.maxReferenceImages) {
      toast.warning(`${videoProviderLabel} 最多支持 ${seedance25Capabilities.maxReferenceImages} 张参考图，请先精简视频输入包`);
      return false;
    }
    if (isSeedance25 && referenceValidation.accepted.length === 0) {
      toast.warning(`${videoProviderLabel} 当前没有可用参考素材，将按纯文本视频生成`);
    }
    const directReferenceUrls = referenceValidation.accepted;
    const rawGuaVideoContents = isSeedance25
      ? []
      : buildGuaVideoContents(packageItems, prompt, { provider: videoProvider });
    const guaVideoContents = isSeedance25
      ? rawGuaVideoContents
      : await preflightVideoAssetContents({
          provider: videoProvider,
          projectId,
          contents: rawGuaVideoContents,
          context: {
            episodeId,
            shotNumber: boardIndex,
            name: firstText(board?.title, `第${boardIndex}张故事板`),
            source: "storyboard",
            sourceDetail: "episode_storyboard_board_video_preflight",
          },
          shouldContinue: () => scopeKeyRef.current === requestScopeKey,
          onStatus: ({ status }) => {
            if (status === "Processing" && scopeKeyRef.current === requestScopeKey) {
              updateBoardVideo(boardIndex, {
                status: "submitting",
                progress: 4,
                error: "正在等待参考图入库",
              });
            }
          },
        });
    const preparedPackageItems = isSeedance25
      ? packageItems
      : mergePreparedVideoAssetMetadata(packageItems, guaVideoContents, videoProvider);
    const submittedPrompt = isSeedance25
      ? normalizeSeedance25ReferencePrompt(videoProvider, prompt, packageItems)
      : (guaVideoContents.find((item) => item.type === "text")?.text || prompt);
    const imageRefs = isSeedance25
      ? directReferenceUrls.map((url) => ({ type: "image_url", url, role: "reference_image" }))
      : guaVideoContents.filter((item) => item.type === "image_url");
    if (!imageRefs.length && !isSeedance25) {
      toast.warning("没有可用图片参考，请在视频输入包里至少保留一张图");
      return false;
    }

    const sanitizedInputPackage = sanitizeVideoAuxiliaryReferenceItems(preparedPackageItems.map((item) => ({
      name: item.name,
      type: item.type,
      role: item.role,
      mediaType: item.mediaType,
      url: item.url,
      ...(isSeedance25 ? {} : {
        guaVideoAssetId: item.guaVideoAssetId,
        guaVideoAssetStatus: item.guaVideoAssetStatus,
        engGuaVideoAssetId: item.engGuaVideoAssetId,
        engGuaVideoAssetStatus: item.engGuaVideoAssetStatus,
        guaOminiAssetId: item.guaOminiAssetId,
        guaOminiAssetStatus: item.guaOminiAssetStatus,
        dreaminaProxyAssetId: item.dreaminaProxyAssetId,
        dreaminaProxyAssetStatus: item.dreaminaProxyAssetStatus,
        assetProvider: item.assetProvider,
        voiceSampleUrl: item.voiceSampleUrl,
        voiceSampleName: item.voiceSampleName,
        voiceBoundAt: item.voiceBoundAt,
      }),
    })), videoProvider);
    const payload = {
      provider: videoProvider,
      model: projectVideoModel,
      prompt: submittedPrompt,
      duration: `${durationSeconds}s`,
      aspectRatio: payloadAspectRatio,
      ratio: payloadAspectRatio,
      resolution,
      projectId: projectId ? Number(projectId) : undefined,
      episodeId: episodeId ? Number(episodeId) : undefined,
      storyboardBoardIndex: boardIndex,
      storyboardVideoInputPackage: sanitizedInputPackage,
      storyboardVideoConfig: {
        ratio: payloadAspectRatio,
        resolution,
        durationSeconds,
        ...(isSeedance25
          ? (seedance25Capabilities.supportsGenerateAudioControl ? { generateAudio } : {})
          : { generateAudio }),
        noSubtitle: draft.noSubtitle ?? false,
        lighting: firstText(draft.lighting),
        referenceCount: imageRefs.length,
        presentationMode,
      },
      ...(isSeedance25
        ? {
          generationMode: imageRefs.length ? "reference" : "text2video",
          ...(seedance25Capabilities.supportsGenerateAudioControl ? { generateAudio } : {}),
          ...buildSeedance25ReferencePayload(videoProvider, packageItems),
        }
        : {
          guaVideoContents,
          guaVideoMode: "ref2v",
          generateAudio,
        }),
    };

    const now = new Date().toISOString();
    stoppedVideoBoardIndexesRef.current.delete(boardIndex);
    markBoardVideoSubmitting(boardIndex, true);
    setError("");
    updateBoardVideo(boardIndex, {
      status: "submitting",
      progress: 3,
      prompt: submittedPrompt,
      provider: videoProvider,
      model: projectVideoModel,
      duration: `${durationSeconds}s`,
      ratio: payloadAspectRatio,
      resolution,
      ...(isSeedance25
        ? (seedance25Capabilities.supportsGenerateAudioControl ? { generateAudio } : {})
        : { generateAudio }),
      noSubtitle: draft.noSubtitle ?? false,
      lighting: firstText(draft.lighting),
      referenceCount: imageRefs.length,
      referenceImagePreviews: summarizeImageRefs(imageRefs),
      inputPackage: sanitizedInputPackage,
      createdAt: now,
      error: "",
    }, { captureCurrent: true, currentLabel: "重新生成前" });
    setMessages((current) => [...current, {
      id: `u-video-${boardIndex}-${Date.now()}`,
      role: "user",
      content: `用第${boardIndex}张故事板生成 ${durationSeconds} 秒视频${requestText ? `：${requestText}` : ""}`,
      createdAt: now,
    }].slice(-40));

    try {
      const response = await submitVideoTask(payload);
      if (scopeKeyRef.current !== requestScopeKey) return false;
      const taskId = response?.taskId;
      if (!taskId) {
        throw new Error("后端未返回视频任务ID");
      }
      if (stoppedVideoBoardIndexesRef.current.has(boardIndex)) {
        stoppedVideoTaskIdsRef.current.add(taskId);
        updateBoardVideo(boardIndex, {
          status: "stopped",
          taskId,
          progress: Number(boardVideos?.[boardIndex]?.progress) || 0,
          error: `已停止前端等待。${videoProviderLabel} 暂无可用取消接口，服务商任务可能仍会继续运行。`,
          stoppedAt: new Date().toISOString(),
        });
        return false;
      }
      updateBoardVideo(boardIndex, {
        status: "processing",
        taskId,
        progress: 8,
      });
      stoppedVideoTaskIdsRef.current.delete(taskId);
      setMessages((current) => [...current, {
        id: `a-video-submit-${boardIndex}-${Date.now()}`,
        role: "assistant",
        content: imageRefs.length
          ? `第${boardIndex}张故事板已提交 ${videoProviderLabel}。第一张参考图是故事板，其后带入 ${Math.max(0, imageRefs.length - 1)} 张人物/场景/道具参考图。`
          : `第${boardIndex}张故事板已提交 ${videoProviderLabel}，当前按纯文本视频生成。`,
        createdAt: new Date().toISOString(),
      }].slice(-40));

      const stopPolling = startPolling(videoProvider, taskId, {
        projectId: projectId ? Number(projectId) : undefined,
        episodeId: episodeId ? Number(episodeId) : undefined,
        storyboardBoardIndex: boardIndex,
        prompt,
        model: projectVideoModel,
        onSuccess: (data) => {
          if (scopeKeyRef.current !== requestScopeKey) return;
          if (stoppedVideoTaskIdsRef.current.has(taskId) || stoppedVideoBoardIndexesRef.current.has(boardIndex)) return;
          videoPollingMapRef.current.delete(taskId);
          updateBoardVideo(boardIndex, {
            ...data,
            status: "success",
            progress: 100,
            videoUrl: data?.videoUrl || data?.url || data?.result?.videoUrl,
            completedAt: new Date().toISOString(),
          }, { nextLabel: "生成完成" });
          markBoardVideoSubmitting(boardIndex, false);
          setMessages((current) => [...current, {
            id: `a-video-success-${boardIndex}-${Date.now()}`,
            role: "assistant",
            content: `第${boardIndex}张故事板的视频已生成完成。`,
            createdAt: new Date().toISOString(),
          }].slice(-40));
          toast.success(`第${boardIndex}张视频生成完成`);
        },
        onError: (errorMsg) => {
          if (scopeKeyRef.current !== requestScopeKey) return;
          if (stoppedVideoTaskIdsRef.current.has(taskId) || stoppedVideoBoardIndexesRef.current.has(boardIndex)) return;
          const normalizedError = normalizeVideoErrorMessage(errorMsg || "视频生成失败");
          videoPollingMapRef.current.delete(taskId);
          updateBoardVideo(boardIndex, {
            status: "failed",
            error: normalizedError,
            completedAt: new Date().toISOString(),
          });
          markBoardVideoSubmitting(boardIndex, false);
          setMessages((current) => [...current, {
            id: `a-video-failed-${boardIndex}-${Date.now()}`,
            role: "assistant",
            content: `第${boardIndex}张视频生成失败：${normalizedError}`,
            createdAt: new Date().toISOString(),
          }].slice(-40));
          toast.error(normalizedError, 0);
        },
        onProgress: ({ progress, status }) => {
          if (scopeKeyRef.current !== requestScopeKey) return;
          if (stoppedVideoTaskIdsRef.current.has(taskId) || stoppedVideoBoardIndexesRef.current.has(boardIndex)) return;
          updateBoardVideo(boardIndex, {
            status: status || "processing",
            progress,
          });
        },
      });
      videoPollingMapRef.current.set(taskId, stopPolling);
      if (!options.keepDirectorRequest) setDirectorRequest("");
      return true;
    } catch (err) {
      if (scopeKeyRef.current !== requestScopeKey) return false;
      const message = normalizeVideoErrorMessage(err.message || "视频提交失败");
      updateBoardVideo(boardIndex, {
        status: "failed",
        error: message,
        completedAt: new Date().toISOString(),
      });
      setMessages((current) => [...current, {
        id: `e-video-${boardIndex}-${Date.now()}`,
        role: "assistant",
        content: `第${boardIndex}张视频提交失败：${message}`,
        createdAt: new Date().toISOString(),
      }].slice(-40));
      toast.error(message, 0);
      return false;
    } finally {
      if (scopeKeyRef.current === requestScopeKey) markBoardVideoSubmitting(boardIndex, false);
    }
  };

  const generateAllVideosHandler = useStableEvent(async () => {
    const requestScopeKey = scopeKey;
    if (!videoGeneratableBoards.length) {
      toast.warning("没有可提交的视频片段：已成功或正在生成的视频不会重复提交");
      return;
    }
    setGeneratingAllVideos(true);
    let submitted = 0;
    let failed = 0;
    try {
      for (const board of videoGeneratableBoards) {
        if (scopeKeyRef.current !== requestScopeKey) return;
        const ok = await generateVideoFromBoard(board, { keepDirectorRequest: true });
        if (ok) submitted += 1;
        else failed += 1;
        await new Promise((resolve) => setTimeout(resolve, 250));
      }
      if (scopeKeyRef.current !== requestScopeKey) return;
      if (submitted > 0) {
        toast.success(`已提交 ${submitted} 个视频片段${failed ? `，跳过/失败 ${failed} 个` : ""}`);
      } else {
        toast.warning("没有视频片段提交成功");
      }
    } finally {
      if (scopeKeyRef.current === requestScopeKey) setGeneratingAllVideos(false);
    }
  });

  const buildVideoDownloadName = useCallback((boardIndex, url) => {
    const dramaName = sanitizeFileNamePart(projectTitle, `项目${projectId || ""}`);
    const episodeName = sanitizeFileNamePart(buildEpisodeDownloadLabel(episode, episodeId));
    const segmentName = `第${String(Number(boardIndex) || 0).padStart(2, "0")}个片段`;
    return `${dramaName}_${episodeName}_${segmentName}${videoFileExtension(url)}`;
  }, [episode, episodeId, projectId, projectTitle]);

  const buildVideoZipDownloadName = useCallback(() => {
    const dramaName = sanitizeFileNamePart(projectTitle, `项目${projectId || ""}`);
    const episodeName = sanitizeFileNamePart(buildEpisodeDownloadLabel(episode, episodeId));
    return `${dramaName}_${episodeName}_视频片段.zip`;
  }, [episode, episodeId, projectId, projectTitle]);

  const triggerDownload = useCallback((url, filename) => {
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = filename;
    anchor.target = "_blank";
    anchor.rel = "noopener";
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
  }, []);

  const downloadVideoWithName = useCallback(async (url, filename) => {
    try {
      const response = await fetch(url, { mode: "cors" });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      triggerDownload(objectUrl, filename);
      setTimeout(() => URL.revokeObjectURL(objectUrl), 30000);
    } catch {
      // 跨域存储桶如果不允许 fetch blob，降级为直接链接下载。
      triggerDownload(url, filename);
    }
  }, [triggerDownload]);

  const downloadBoardVideoHistoryHandler = useStableEvent(async (board, entry) => {
    const boardIndex = Number(board?.index || entry?.boardIndex);
    const videoUrl = firstText(entry?.videoUrl, entry?.url, entry?.resultUrl);
    if (!boardIndex || !videoUrl) {
      toast.warning("这个视频版本没有可下载地址");
      return;
    }
    await downloadVideoWithName(videoUrl, buildVideoDownloadName(boardIndex, videoUrl));
  });

  const downloadAllVideosHandler = useStableEvent(async () => {
    if (!downloadableVideos.length) {
      toast.warning("还没有已完成的视频可下载");
      return;
    }
    setDownloadingAllVideos(true);
    try {
      const params = new URLSearchParams({
        projectId: String(projectId),
        episodeId: String(episodeId),
        dramaName: firstText(projectTitle, `项目${projectId || ""}`),
        episodeName: buildEpisodeDownloadLabel(episode, episodeId),
      });
      const response = await fetch(`${API_HOST}/api/episode-storyboard-board/videos/download?${params.toString()}`);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      triggerDownload(objectUrl, buildVideoZipDownloadName());
      setTimeout(() => URL.revokeObjectURL(objectUrl), 30000);
      toast.success(`已下载 ${downloadableVideos.length} 个视频片段压缩包`);
    } catch {
      for (const item of downloadableVideos) {
        await downloadVideoWithName(item.url, buildVideoDownloadName(item.boardIndex, item.url));
        await new Promise((resolve) => setTimeout(resolve, 350));
      }
      toast.success(`压缩包下载失败，已改为逐个触发下载 ${downloadableVideos.length} 个视频片段`);
    } finally {
      setDownloadingAllVideos(false);
    }
  });

  const stopVideoGeneration = useCallback((board) => {
    const boardIndex = Number(board?.index);
    if (!boardIndex) return;
    const currentVideo = boardVideos?.[boardIndex] || {};
    const taskId = currentVideo.taskId;
    stoppedVideoBoardIndexesRef.current.add(boardIndex);
    if (taskId) {
      stoppedVideoTaskIdsRef.current.add(taskId);
      videoPollingMapRef.current.get(taskId)?.();
      videoPollingMapRef.current.delete(taskId);
    }
    updateBoardVideo(boardIndex, {
      status: "stopped",
      progress: Number(currentVideo.progress) || 0,
      error: `已停止前端等待。${videoProviderLabel} 暂无可用取消接口，服务商任务可能仍会继续运行。`,
      stoppedAt: new Date().toISOString(),
    });
    markBoardVideoSubmitting(boardIndex, false);
    setMessages((current) => [...current, {
      id: `a-video-stop-${boardIndex}-${Date.now()}`,
      role: "assistant",
      content: `已停止等待第${boardIndex}张视频结果。需要继续时，可以重新提交这一张。`,
      createdAt: new Date().toISOString(),
    }].slice(-40));
    toast.info(`已停止等待第${boardIndex}张视频`);
  }, [boardVideos, markBoardVideoSubmitting, toast, updateBoardVideo, videoProviderLabel]);

  const eraseSubtitleForBoard = async (board) => {
    const requestScopeKey = scopeKey;
    if (scopeKeyRef.current !== requestScopeKey) return;
    const boardIndex = Number(board?.index);
    const currentVideo = boardVideos?.[boardIndex] || {};
    const originalVideoUrl = firstText(currentVideo.originalVideoUrl);
    if (!boardIndex) {
      toast.warning("请先选择有效故事板");
      return;
    }
    if (!originalVideoUrl) {
      toast.warning("缺少方舟原始视频链接，无法去字幕");
      return;
    }
    if (currentVideo.postProcessStatus === "erasing_subtitle") {
      toast.info("这条视频正在去字幕");
      return;
    }

    updateBoardVideo(boardIndex, {
      postProcessStatus: "erasing_subtitle",
      postProcessError: "",
    });
    toast.info(`第${boardIndex}张视频去字幕任务已提交`);

    try {
      const response = await submitEraseSubtitle({ videoUrl: originalVideoUrl });
      if (scopeKeyRef.current !== requestScopeKey) return;
      const taskId = response?.taskId;
      if (!taskId) throw new Error("后端未返回去字幕任务ID");

      updateBoardVideo(boardIndex, {
        postProcessTaskId: taskId,
      });

      const stopPolling = startPostProcessPolling("erase-subtitle", taskId, {
        interval: 5000,
        onSuccess: (data) => {
          if (scopeKeyRef.current !== requestScopeKey) return;
          videoPollingMapRef.current.delete(`erase-${taskId}`);
          const nextVideoUrl = firstText(data?.videoUrl);
          const nextVideoPatch = {
            videoUrl: nextVideoUrl || currentVideo.videoUrl,
            url: nextVideoUrl || currentVideo.url,
            originalVideoUrl: "",
            status: "success",
            postProcessStatus: "success",
            postProcessTaskId: taskId,
            erasedSubtitle: true,
            completedAt: new Date().toISOString(),
          };
          updateBoardVideo(boardIndex, nextVideoPatch, { captureCurrent: true, currentLabel: "去字幕前", nextLabel: "去字幕后" });
          syncBoardVideoRecord(boardIndex, nextVideoPatch);
          toast.success(`第${boardIndex}张视频去字幕完成`);
        },
        onError: (msg) => {
          if (scopeKeyRef.current !== requestScopeKey) return;
          videoPollingMapRef.current.delete(`erase-${taskId}`);
          updateBoardVideo(boardIndex, {
            postProcessStatus: "failed",
            postProcessError: msg || "去字幕失败",
          });
          toast.error(msg || "去字幕失败", 0);
        },
        onProgress: () => {},
      });
      videoPollingMapRef.current.set(`erase-${taskId}`, stopPolling);
    } catch (err) {
      if (scopeKeyRef.current !== requestScopeKey) return;
      updateBoardVideo(boardIndex, {
        postProcessStatus: "failed",
        postProcessError: err.message || "提交去字幕失败",
      });
      toast.error(err.message || "提交去字幕失败", 0);
    }
  };

  const generateBoards = async (requestOverride = "", options = {}) => {
    const requestScopeKey = scopeKey;
    if (scopeKeyRef.current !== requestScopeKey) return;
    if (itemTaskBusy) {
      toast.warning("还有单张补图或视频任务在进行中，等它结束后再整集重排");
      return;
    }
    if (!episode) {
      toast.warning("请先选择有效单集");
      return;
    }
    if (!scriptText.trim()) {
      toast.warning("当前集没有剧本，先去剧本专区拆出本集剧本");
      return;
    }

    const requestText = firstText(
      requestOverride,
      directorRequest,
      presentationMode === "grid9"
        ? "请生成黑白线稿无文字连续九宫格故事板：每张图为3x3九宫格，九个画格连续推进同一段戏；画面内不要任何文字、字幕、编号、对白气泡或OS。每张第1格承接上一张第9格的站位和动作状态，每张第9格作为下一张第1格的开端锚点，重点保持演员站位、视线、调度和镜头轴线连贯。"
        : presentationMode === "blocking"
        ? "请生成模糊站位故事板：使用黑白线稿/框架草图，不出现真人五官和写实人脸；重点锁定每段分镜、人物编号、场景坐标、站位、视线、移动路径、镜头轴线和景别变化。故事板图里不要写完整对白、OS或字幕，完整台词仍以后续视频提示词为准。"
        : presentationMode === "narration"
        ? "请按解说剧模式生成连续故事板：每张约15秒，以一个旁白贯穿讲述剧情，中间只保留少量关键对白；保持人物、场景、道具和情绪连续，不要删桥段。"
        : "请根据本集剧本和已选参考图，先判断本集实际需要多少张连续故事板，再按拆分结果生成故事板图。每张图控制15秒，前后保持人物、场景、道具和情绪连续；如果剧本文字较多，请自动增加故事板数量，不要删台词或删桥段；如果剧情很短，不要为了凑数生成空页。"
    );
    const userMessage = {
      id: `u-${Date.now()}`,
      role: "user",
      content: requestText,
      createdAt: new Date().toISOString(),
    };
    const resetGenerationContext = Boolean(options.resetContext);
    const nextMessages = resetGenerationContext
      ? [userMessage]
      : options.skipUserMessage
        ? messages
        : [...messages, userMessage].slice(-40);
    if (resetGenerationContext || !options.skipUserMessage) setMessages(nextMessages);
    setPendingDirectorAction(null);
    setLoading("generate");
    setError("");
    const previousBoardImageUrl = presentationMode === "grid9" || resetGenerationContext
      ? ""
      : normalizeImageUrl(boards.find((board) => board?.imageUrl)?.imageUrl || "");
    const conversationHistoryForRequest = resetGenerationContext ? [] : buildStoryboardConversationHistory(nextMessages);
    let assetGroupsForRequest;
    try {
      assetGroupsForRequest = await getAssetsForGeneration();
    } catch (assetError) {
      if (scopeKeyRef.current === requestScopeKey) {
        setLoading("");
        toast.error(assetError.message || "加载故事板参考资产失败", 0);
      }
      return;
    }
    if (scopeKeyRef.current !== requestScopeKey) return;
    const videoRefsForRequest = buildSelectedRefsFromKeys(assetGroupsForRequest, selectedRefKeysRef.current);
    const refsForRequest = limitSelectedReferenceGroups(videoRefsForRequest, REF_LIMIT);
    const manualSegmentsForRequest = segmentationMode === "manual"
      ? (normalizedManualSegments.length >= BOARD_COUNT ? normalizedManualSegments : buildManualStoryboardSegments(scriptText, BOARD_COUNT, BOARD_SECONDS, presentationMode))
      : [];
    const storyboardCount = segmentationMode === "manual"
      ? Math.max(1, manualSegmentsForRequest.length)
      : 0;
    if (segmentationMode === "manual" && !manualSegmentsForRequest.length) {
      setLoading("");
      toast.warning("请先确认本集手动拆段");
      return;
    }
    // 新线路在清空当前画板之前检查全部参考与文字子模型，不因失败覆盖现有成果。
    try {
      resolveStoryboardBoardRequest({
        segmentationMode,
        characterRefs: videoRefsForRequest.characters,
        sceneRefs: videoRefsForRequest.scenes,
        propRefs: videoRefsForRequest.props,
        previousBoardImageUrl,
      });
    } catch (referenceError) {
      setLoading('');
      setError(referenceError.message);
      toast.warning(referenceError.message);
      return;
    }
    const archivedBoardImageHistories = archiveBoardImageVersions(boardImageHistories, boards);
    const archivedBoardVideos = archiveBoardVideoVersions(boardVideos);
    setBoardImageHistories(archivedBoardImageHistories);
    setBoards(storyboardCount > 0 ? createPlaceholderBoards(storyboardCount, BOARD_SECONDS) : []);
    setBoardVideos(archivedBoardVideos);
    setVideoDrafts({});
    setOpenVideoOptionsIndexes(new Set());
    videoPollingMapRef.current.forEach((stopPolling) => stopPolling?.());
    videoPollingMapRef.current.clear();
    const streamAbortController = new AbortController();
    let submittedBoardGenerationTaskId = "";
    let recoveredFromPersistedBoards = false;
    let generationTerminal = false;
    let expectedBoardCount = storyboardCount;
    const generationFreshAfter = Date.now() - 5000;
    const isTerminalBoard = (board) => ["success", "failed"].includes(board?.status) || normalizeImageUrl(board?.imageUrl);
    const recoverPersistedBoards = async () => {
      if (scopeKeyRef.current !== requestScopeKey) return [];
      const persisted = await refreshPersistedBoards({ freshAfter: generationFreshAfter });
      if (scopeKeyRef.current !== requestScopeKey) return [];
      const terminalCount = persisted.filter((board) => {
        const updatedAt = Date.parse(board?.updatedAt || board?.createdAt || "");
        return updatedAt && updatedAt >= generationFreshAfter && isTerminalBoard(board);
      }).length;
      if (expectedBoardCount > 0 && terminalCount >= expectedBoardCount) {
        recoveredFromPersistedBoards = true;
        generationTerminal = true;
        setLoading((current) => (current === "generate" ? "" : current));
        streamAbortController.abort();
      }
      return persisted;
    };
    const recoveryTimer = window.setInterval(() => {
      recoverPersistedBoards().catch(() => {});
    }, 12000);

    try {
      let finalSummary = null;
      await postStoryboardBoardStream({
        projectId: Number(projectId),
        episodeId: Number(episodeId),
        episodeTitle: episode.title || "",
        script: scriptText,
        aspectRatio,
        ...(storyboardCount > 0 ? { count: storyboardCount } : {}),
        durationSecondsPerBoard: BOARD_SECONDS,
        segmentationMode,
        presentationMode,
        manualSegments: manualSegmentsForRequest,
        userRequest: requestText,
        stylePrompt: storyboardStylePrompt,
        previousBoardImageUrl,
        characterRefs: refsForRequest.characters,
        sceneRefs: refsForRequest.scenes,
        propRefs: refsForRequest.props,
        conversationHistory: conversationHistoryForRequest,
      }, {
        start: (info) => {
          if (scopeKeyRef.current !== requestScopeKey) return;
          expectedBoardCount = Number(info?.count) || storyboardCount;
          if (expectedBoardCount > 0) {
            setBoards((current) => ensureStoryboardBoardSlots(current, expectedBoardCount, info?.durationSecondsPerBoard || BOARD_SECONDS));
          }
        },
        board: (board) => {
          if (scopeKeyRef.current !== requestScopeKey) return;
          mergeBoardWithHistory(board);
          if (board?.status === "failed") {
            toast.warning(`第${board.index || ""}张故事板生成失败，已保留占位`);
          }
        },
        done: (summary) => {
          if (scopeKeyRef.current !== requestScopeKey) return;
          generationTerminal = true;
          finalSummary = summary || {};
          const successCount = Number(finalSummary.successCount) || 0;
          const failedCount = Number(finalSummary.failedCount) || 0;
          const totalCount = Number(finalSummary.count) || successCount + failedCount || storyboardCount;
          const secondsPerBoard = Number(finalSummary.durationSecondsPerBoard) || BOARD_SECONDS;
          setMessages((current) => [...current, {
            id: `a-${Date.now()}`,
            role: "assistant",
            content: `本轮故事板已完成：成功 ${successCount} 张，失败 ${failedCount} 张。每张控制 ${secondsPerBoard} 秒，本轮共 ${totalCount} 张，视频目标约 ${totalCount * secondsPerBoard} 秒。你可以继续要求我只重做某一张，或明确选择整集重排。`,
            createdAt: new Date().toISOString(),
          }].slice(-40));
          if (failedCount > 0) {
            toast.warning(`已生成 ${successCount} 张，${failedCount} 张失败占位`);
          } else {
            toast.success(`已生成 ${successCount} 张连续故事板图`);
          }
        },
      }, {
        signal: streamAbortController.signal,
        onSubmitted: (task) => {
          if (scopeKeyRef.current !== requestScopeKey) {
            streamAbortController.abort();
            return;
          }
          submittedBoardGenerationTaskId = task.taskId;
          const activeTask = {
            taskId: task.taskId,
            internalTaskId: task.internalTaskId,
            status: task.status || "processing",
            ...(storyboardCount > 0 ? { count: storyboardCount } : {}),
            durationSecondsPerBoard: BOARD_SECONDS,
            startedAt: new Date().toISOString(),
            requestText,
            presentationMode,
            segmentationMode,
          };
          boardGenerationPollingRef.current = {
            taskId: task.taskId,
            stop: () => streamAbortController.abort(),
          };
          const immediatePayload = {
            ...memoryPayload,
            messages: nextMessages,
            pendingDirectorAction: null,
            boardVideos: archivedBoardVideos,
            boardImageHistories: archivedBoardImageHistories,
            videoDrafts: {},
            openVideoOptionsIndexes: [],
            activeBoardGenerationTask: activeTask,
          };
          if (saveStateTimerRef.current) clearTimeout(saveStateTimerRef.current);
          saveMemory(projectId, episodeId, immediatePayload);
          queueStoryboardStateSave(projectId, episodeId, requestScopeKey, immediatePayload).catch(() => {});
          setActiveBoardGenerationTask(activeTask);
        },
        onProgress: (status) => {
          if (scopeKeyRef.current !== requestScopeKey) return;
          const currentTaskId = submittedBoardGenerationTaskId || boardGenerationPollingRef.current?.taskId;
          setActiveBoardGenerationTask((current) => (
            current?.taskId === currentTaskId
              ? {
                ...current,
                status: status?.status || current.status || "processing",
                progress: status?.progress,
                currentStage: status?.currentStage,
                currentStageText: status?.currentStageText,
                updatedAt: new Date().toISOString(),
              }
              : current
          ));
        },
      });
      if (scopeKeyRef.current !== requestScopeKey) return;
      if (!finalSummary) {
        setMessages((current) => [...current, {
          id: `a-${Date.now()}`,
          role: "assistant",
          content: "本轮故事板连接已结束。已经返回的图片会保留在列表里，未返回的位置会继续显示占位。",
          createdAt: new Date().toISOString(),
        }].slice(-40));
      }
      setDirectorRequest("");
    } catch (err) {
      if (scopeKeyRef.current !== requestScopeKey) return;
      if (err?.name === "AbortError" && recoveredFromPersistedBoards) {
        generationTerminal = true;
        return;
      }
      const message = err.message || "连续故事板生成失败";
      if (err?.recoverable && submittedBoardGenerationTaskId) {
        setActiveBoardGenerationTask((current) => (
          current?.taskId === submittedBoardGenerationTaskId
            ? {
              ...current,
              status: "processing",
              recoveryError: message,
              updatedAt: new Date().toISOString(),
            }
            : current
        ));
        toast.warning("故事板任务仍在后台执行，正在切换到恢复轮询");
        return;
      }
      generationTerminal = true;
      setBoards((current) => markUnfinishedStoryboardBoardsFailed(current, expectedBoardCount || storyboardCount, BOARD_SECONDS, message));
      setMessages((current) => [...current, {
        id: `e-${Date.now()}`,
        role: "assistant",
        content: `生成失败：${message}`,
        createdAt: new Date().toISOString(),
      }].slice(-40));
      toast.error(message, 0);
    } finally {
      window.clearInterval(recoveryTimer);
      if (scopeKeyRef.current === requestScopeKey) {
        await refreshPersistedBoards().catch(() => {});
        if (!submittedBoardGenerationTaskId || boardGenerationPollingRef.current?.taskId === submittedBoardGenerationTaskId) {
          boardGenerationPollingRef.current = null;
        }
        if (generationTerminal || !submittedBoardGenerationTaskId) {
          setActiveBoardGenerationTask(null);
          setLoading("");
        } else {
          setLoading("generate");
          setBoardGenerationResumeNonce((value) => value + 1);
        }
      }
    }
  };

  const regenerateBoard = async (board, requestOverride = "", options = {}) => {
    const requestScopeKey = scopeKey;
    if (scopeKeyRef.current !== requestScopeKey) return;
    const boardIndex = Number(board?.index);
    if (!episode || !boardIndex) {
      toast.warning("请先选择有效故事板");
      return;
    }
    if (!scriptText.trim()) {
      toast.warning("当前集没有剧本，先去剧本专区拆出本集剧本");
      return;
    }
    if (initialGenerating) {
      toast.warning("整集故事板正在生成，请稍等");
      return;
    }
    if (regeneratingBoardIndexes.has(boardIndex)) {
      toast.warning(`第${boardIndex}张正在重新生图，请稍等`);
      return;
    }
    if (VIDEO_BUSY_STATUSES.has(boardVideos?.[boardIndex]?.status) || generatingVideoBoardIndexes.has(boardIndex)) {
      toast.warning(`第${boardIndex}张视频正在生成，请先停止或等待完成`);
      return;
    }

    const previousBoard = boards.find((item) => Number(item?.index) === boardIndex - 1);
    const existingVideoTaskId = boardVideos?.[boardIndex]?.taskId;
    const requestText = firstText(
      requestOverride,
      directorRequest,
      `只重新生成第${boardIndex}张故事板。保持它在整集连续故事板中的位置，承接上一张，并为下一张保留自然过渡。`
    );
    const userMessage = {
      id: `u-regen-${boardIndex}-${Date.now()}`,
      role: "user",
      content: `重生成第${boardIndex}张：${requestText}`,
      createdAt: new Date().toISOString(),
    };
    const nextMessages = options.skipUserMessage ? messages : [...messages, userMessage].slice(-40);
    if (!options.skipUserMessage) setMessages(nextMessages);
    setPendingDirectorAction(null);
    markBoardRegenerating(boardIndex, true);
    setError("");
    let assetGroupsForRequest;
    try {
      assetGroupsForRequest = await getAssetsForGeneration();
    } catch (assetError) {
      if (scopeKeyRef.current === requestScopeKey) {
        markBoardRegenerating(boardIndex, false);
        toast.error(assetError.message || "加载故事板参考资产失败", 0);
      }
      return;
    }
    if (scopeKeyRef.current !== requestScopeKey) return;
    const latestSelectedRefs = limitSelectedReferenceGroups(
      buildSelectedRefsFromKeys(assetGroupsForRequest, selectedRefKeysRef.current),
      REF_LIMIT,
    );
    const manualSegmentsForRequest = segmentationMode === "manual"
      ? (normalizedManualSegments.length >= BOARD_COUNT ? normalizedManualSegments : buildManualStoryboardSegments(scriptText, BOARD_COUNT, BOARD_SECONDS, presentationMode))
      : [];
    const actualBoardCountForRequest = Math.max(
      boards.length || 0,
      manualSegmentsForRequest.length || 0,
      boardIndex
    );

    try {
      const data = await regenerateStoryboardBoard({
        projectId: Number(projectId),
        episodeId: Number(episodeId),
        episodeTitle: episode.title || "",
        script: scriptText,
        aspectRatio,
        count: actualBoardCountForRequest,
        durationSecondsPerBoard: BOARD_SECONDS,
        segmentationMode,
        presentationMode,
        manualSegments: manualSegmentsForRequest,
        boardIndex,
        reuseReferenceSnapshot: true,
        userRequest: requestText,
        stylePrompt: storyboardStylePrompt,
        regenerateReason: requestText,
        previousBoardImageUrl: presentationMode === "grid9" ? "" : normalizeImageUrl(previousBoard?.imageUrl || ""),
        characterRefs: latestSelectedRefs.characters,
        sceneRefs: latestSelectedRefs.scenes,
        propRefs: latestSelectedRefs.props,
        conversationHistory: buildStoryboardConversationHistory(nextMessages),
      });
      if (scopeKeyRef.current !== requestScopeKey) return;
      const nextBoard = data?.board;
      if (!nextBoard) {
        throw new Error("后端没有返回故事板结果");
      }
      if (existingVideoTaskId) {
        videoPollingMapRef.current.get(existingVideoTaskId)?.();
        videoPollingMapRef.current.delete(existingVideoTaskId);
      }
      setBoardVideos((current) => {
        const next = { ...current };
        delete next[boardIndex];
        return next;
      });
      setVideoDrafts((current) => ({
        ...current,
        [boardIndex]: {
          ...(current?.[boardIndex] || {}),
          prompt: "",
        },
      }));
      mergeBoardWithHistory(nextBoard);
      setMessages((current) => [...current, {
        id: `a-regen-${boardIndex}-${Date.now()}`,
        role: "assistant",
        content: `第${boardIndex}张故事板已重新生成。已继续带入本集剧本、对话历史、当前本集参考图，并用前一张故事板作为连续性锚点。`,
        createdAt: new Date().toISOString(),
      }].slice(-40));
      setDirectorRequest("");
      toast.success(`第${boardIndex}张故事板已重新生成`);
    } catch (err) {
      if (scopeKeyRef.current !== requestScopeKey) return;
      const message = err.message || "故事板重新生成失败";
      setMessages((current) => [...current, {
        id: `e-regen-${boardIndex}-${Date.now()}`,
        role: "assistant",
        content: `第${boardIndex}张重新生成失败：${message}`,
        createdAt: new Date().toISOString(),
      }].slice(-40));
      toast.error(message, 0);
    } finally {
      if (scopeKeyRef.current === requestScopeKey) markBoardRegenerating(boardIndex, false);
    }
  };

  const generateAllWithConfirm = async (requestOverride = "", options = {}) => {
    if (fullGenerationBusy) {
      toast.warning(itemTaskBusy ? "还有单张补图或视频任务在进行中，结束后再整集重排" : "整集故事板正在生成");
      return;
    }
    const resetContext = hasGeneratedBoards || Boolean(options.resetContext);
    if (hasGeneratedBoards) {
      const ok = (await appConfirm("这会重新生成整集故事板，并清空当前视频结果、视频输入包配置、旧导演上下文和旧故事板连续性锚点。是否继续？"));
      if (!ok) return;
    }
    generateBoards(requestOverride, { ...options, resetContext });
  };

  const sendDirectorMessage = () => {
    if (directorBusy) {
      toast.warning("导演正在处理上一条任务，请稍等");
      return;
    }
    if (!hasGeneratedBoards) {
      generateBoards();
      return;
    }
    const requestText = firstText(directorRequest);
    if (!requestText) {
      toast.warning("请先输入要和导演沟通的内容");
      return;
    }
    const intent = detectDirectorStoryboardIntent(requestText, boards.length);
    const action = {
      id: `action-${Date.now()}`,
      mode: intent.mode,
      boardIndex: intent.boardIndex,
      requestText,
      createdAt: new Date().toISOString(),
    };
    const assistantContent = intent.mode === "single"
      ? `我理解你想修改第${intent.boardIndex}张故事板。为了避免误刷新整集，我先不执行；你确认后我只重生成这一张，并继续带上本集剧本、参考图和前一张故事板。`
      : intent.mode === "all"
        ? "我理解这是整集重排请求。整集重排会覆盖当前整集故事板和视频结果，并且只使用你这次输入的要求，不继承旧导演对话。"
        : "我先把这条当成导演修改意见记录下来。你可以选择整集重排，也可以回到某张故事板点“重生图”只改单张。";
    const nextMessages = [
      ...messages,
      {
        id: `u-director-${Date.now()}`,
        role: "user",
        content: requestText,
        createdAt: new Date().toISOString(),
      },
      {
        id: `a-director-${Date.now()}`,
        role: "assistant",
        content: assistantContent,
        createdAt: new Date().toISOString(),
      },
    ].slice(-40);
    setMessages(nextMessages);
    setPendingDirectorAction(action);
    setDirectorRequest("");
  };

  const executePendingSingleBoard = () => {
    if (!pendingDirectorAction?.boardIndex) return;
    const targetBoard = boards.find((item) => Number(item?.index) === Number(pendingDirectorAction.boardIndex));
    if (!targetBoard) {
      toast.warning(`没有找到第${pendingDirectorAction.boardIndex}张故事板`);
      return;
    }
    regenerateBoard(targetBoard, pendingDirectorAction.requestText, { skipUserMessage: true });
  };

  const executePendingAllBoards = () => {
    if (!pendingDirectorAction) return;
    generateAllWithConfirm(pendingDirectorAction.requestText, { skipUserMessage: true });
  };

  const keepPendingAsNote = () => {
    if (!pendingDirectorAction) return;
    setMessages((current) => [...current, {
      id: `a-note-${Date.now()}`,
      role: "assistant",
      content: "已作为导演备注保留，不会修改现有故事板。需要执行时，可以在具体故事板卡片上点“重生图”，或点击整集重排。",
      createdAt: new Date().toISOString(),
    }].slice(-40));
    setPendingDirectorAction(null);
  };

  const cancelPendingDirectorAction = useStableEvent(() => {
    setPendingDirectorAction(null);
  });
  const sendDirectorMessageHandler = useStableEvent(sendDirectorMessage);
  const executePendingSingleBoardHandler = useStableEvent(executePendingSingleBoard);
  const executePendingAllBoardsHandler = useStableEvent(executePendingAllBoards);
  const keepPendingAsNoteHandler = useStableEvent(keepPendingAsNote);

  const regenerateBoardHandler = useStableEvent(regenerateBoard);
  const generateVideoFromBoardHandler = useStableEvent(generateVideoFromBoard);
  const stopVideoGenerationHandler = useStableEvent(stopVideoGeneration);
  const eraseSubtitleForBoardHandler = useStableEvent(eraseSubtitleForBoard);
  const uploadPackageItemToGuaAssetHandler = useStableEvent(uploadPackageItemToGuaAsset);
  const toggleAssetHandler = useStableEvent(toggleAsset);
  const removeSelectedRefHandler = useStableEvent(removeSelectedRef);
  const selectMatchedAssetsHandler = useStableEvent(selectMatchedAssets);
  const clearRefsHandler = useStableEvent(clearRefs);
  const openReferencePickerHandler = useStableEvent(() => setPickerOpen(true));
  const openVideoReferencePickerHandler = useStableEvent(openVideoReferencePicker);
  const toggleVideoOptions = useCallback((boardIndex) => {
    startTransition(() => {
      setOpenVideoOptionsIndexes((current) => {
        const next = new Set(current);
        const normalized = Number(boardIndex);
        if (!normalized) return next;
        if (next.has(normalized)) next.delete(normalized);
        else next.add(normalized);
        return next;
      });
    });
  }, []);

  const changeSegmentationMode = useCallback((mode) => {
    setSegmentationMode(mode === "manual" ? "manual" : "ai");
    if (mode === "manual" && scriptText.trim() && !normalizedManualSegments.length) {
      setManualSegments(buildManualStoryboardSegments(scriptText, BOARD_COUNT, BOARD_SECONDS, presentationMode));
    }
  }, [normalizedManualSegments.length, presentationMode, scriptText]);

  const directorVideoTotalDuration = useMemo(
    () => videoGeneratableBoards.reduce((total, board) => {
      const boardIndex = Number(board?.index);
      const draft = videoDrafts?.[boardIndex] || {};
      const rawDuration = draft?.duration ?? board?.durationSeconds ?? BOARD_SECONDS;
      return total + (isSeedance25Provider(videoProvider)
        ? normalizeSeedance25Duration(videoProvider, rawDuration)
        : normalizeVideoDuration(rawDuration));
    }, 0),
    [videoDrafts, videoGeneratableBoards, videoProvider]
  );
  const directorVideoCostRequest = videoGeneratableBoards.length ? {
    type: "video",
    model: videoProvider,
    subModel: projectVideoModel,
    resolution: globalVideoResolution,
    totalDurationSeconds: Math.max(1, Math.round(directorVideoTotalDuration)),
    count: videoGeneratableBoards.length,
    promptCharacters: videoGeneratableBoards.reduce(
      (total, board) => total + String(board?.videoPrompt || board?.prompt || "").length,
      0
    ),
    source: "storyboard_director_batch_video",
  } : null;
  const estimatedDirectorBoardCount = Math.max(
    1,
    boards.length
      || normalizedManualSegments.length
      || Math.min(60, Math.ceil(scriptText.trim().length / 1_600))
  );
  const directorImageProvider = getCurrentImageProvider();
  const directorImageResolution = normalizeWanxImageQualityTier(
    /gpt|openai|huobao/i.test(directorImageProvider) ? "2K" : "4K",
    directorImageProvider,
  );
  const directorBoardCostRequest = scriptText.trim() ? {
    type: "image",
    model: directorImageProvider,
    resolution: directorImageResolution,
    count: estimatedDirectorBoardCount,
    promptCharacters: scriptText.length,
    referenceImageCount: Math.min(REF_LIMIT, selectedRefCount),
    source: "storyboard_director_board_image",
  } : null;

  return (
    <div className="w-full h-full overflow-hidden flex flex-col" style={{ backgroundColor: SURFACE.page }}>
      <header className="min-h-14 shrink-0 border-b px-3 py-2 flex flex-wrap items-center justify-between gap-2" style={{ borderColor: "var(--color-border-primary)", backgroundColor: SURFACE.panel }}>
        <div className="flex items-center gap-3 min-w-0">
          <span className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: "var(--color-accent-light)", color: theme.colors.accentPrimary }}>
            <Clapperboard size={17} />
          </span>
          <div className="min-w-0">
            <div className="text-sm font-bold truncate" style={{ color: "var(--color-text-primary)" }}>故事板导演台</div>
            <div className="text-xs truncate" style={{ color: "var(--color-text-muted)" }}>
              {episode?.title || "当前集"} · 按剧情自动拆分连续 production board，每张控制 15 秒
            </div>
          </div>
        </div>
        <div className="flex flex-wrap items-center justify-end gap-1.5 shrink-0">
          <div className="flex flex-wrap items-center justify-end gap-1.5">
            <div className="flex items-center gap-1 rounded-lg border p-1" style={{ borderColor: "var(--color-border-primary)", backgroundColor: SURFACE.card }}>
              {STORYBOARD_PRESENTATION_MODE_OPTIONS.map((item) => (
                <button
                  key={item.value}
                  type="button"
                  onClick={() => setPresentationMode(item.value)}
                  className="h-7 px-2 rounded-md text-xs font-semibold cursor-pointer"
                  style={{
                    backgroundColor: presentationMode === item.value ? theme.colors.accentPrimary : "transparent",
                    color: presentationMode === item.value ? "#fff" : "var(--color-text-secondary)",
                  }}
                  title={item.title}
                >
                  {item.label}
                </button>
              ))}
            </div>
            <AppSelect
              value={videoProviderOverride || "project_default"}
              onChange={(event) => changeVideoProviderOverride(event.target.value)}
              className="h-8 rounded-lg border px-2 text-xs font-semibold outline-none"
              style={{ borderColor: "var(--color-border-primary)", backgroundColor: SURFACE.card, color: "var(--color-text-primary)" }}
              title="切换当前视频生成线路及其目标素材库；项目默认值与另一库记录保持不变"
            >
              <option value="project_default">{getVideoProviderLabel(projectRuntimeVideoProvider)}（项目默认）</option>
              <option value={VIDEO_PROVIDER_VOLCENGINE_SEEDANCE}>Seedance 2.0 / Fast / Mini（国内官方）</option>
              {getSeedance25Capabilities(videoProviderOverride)?.retired && <option value={videoProviderOverride} disabled>{getVideoProviderLabel(videoProviderOverride)} · 已停用，请选择官方渠道</option>}
              {SEEDANCE25_PROVIDER_OPTIONS.map((item) => (
                <option key={item.value} value={item.value}>{item.label}</option>
              ))}
            </AppSelect>
            {isDomesticOfficialSeedance(videoProvider) && <AppSelect value={officialDomesticModel} onChange={(event) => setOfficialDomesticModel(event.target.value)} className="h-8 rounded-lg border px-2 text-xs outline-none" style={{ borderColor: 'var(--color-border-primary)', backgroundColor: SURFACE.card, color: 'var(--color-text-primary)' }} title="国内官方模型版本">
              {DOMESTIC_SEEDANCE_MODELS.map((model) => <option key={model.id} value={model.id}>{model.name}</option>)}
            </AppSelect>}
            <AppSelect
              value={aspectRatio}
              onChange={(event) => setAspectRatio(event.target.value)}
              className="h-8 rounded-lg border px-2 text-xs outline-none"
              style={{ borderColor: "var(--color-border-primary)", backgroundColor: SURFACE.card, color: "var(--color-text-primary)" }}
              title="故事板画幅"
            >
              <option value="16:9">16:9</option>
              <option value="21:9">21:9</option>
              <option value="9:16">9:16</option>
              <option value="1:1">1:1</option>
            </AppSelect>
            <div className="flex items-center gap-1 rounded-lg border p-1" style={{ borderColor: "var(--color-border-primary)", backgroundColor: SURFACE.card }}>
              {videoResolutionOptions.map((item) => (
                <button
                  key={item}
                  type="button"
                  onClick={() => applyGlobalVideoResolution(item)}
                  disabled={hasSingleVideoResolution}
                  className="h-7 px-2 rounded-md text-xs font-semibold cursor-pointer"
                  style={{
                    backgroundColor: globalVideoResolution === item ? theme.colors.accentPrimary : "transparent",
                    color: globalVideoResolution === item ? "#fff" : "var(--color-text-secondary)",
                  }}
                  title="设置本集所有故事板视频配置的默认分辨率"
                >
                  {item}
                </button>
              ))}
            </div>
            <div className="inline-flex rounded-lg border p-1" style={{ borderColor: "var(--color-border-primary)", backgroundColor: SURFACE.card }}>
              {[
                ["ai", "AI拆段"],
                ["manual", "手动"],
              ].map(([value, label]) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => changeSegmentationMode(value)}
                  disabled={directorBusy}
                  className="h-7 px-2 rounded-md text-xs font-semibold cursor-pointer disabled:opacity-50"
                  style={{
                    backgroundColor: segmentationMode === value ? theme.colors.accentPrimary : "transparent",
                    color: segmentationMode === value ? "#fff" : "var(--color-text-secondary)",
                  }}
                  title={value === "manual" ? "手动确认每张故事板对应剧本段落" : "由 AI 自动拆分故事板段落"}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
          <div className="h-7 w-px" style={{ backgroundColor: "var(--color-border-primary)" }} />
          <button
            type="button"
            onClick={loadData}
            disabled={loading === "load"}
            className="h-8 px-2.5 rounded-lg border text-xs font-semibold inline-flex items-center gap-1.5 cursor-pointer disabled:opacity-50"
            style={{ borderColor: "var(--color-border-primary)", backgroundColor: SURFACE.card, color: "var(--color-text-primary)" }}
          >
            <RefreshCw size={14} className={loading === "load" ? "animate-spin" : ""} />
            刷新
          </button>
          {assetIngestSupported && <button
            type="button"
            onClick={uploadAllBoardAssetsHandler}
            disabled={uploadingAllBoardAssets || !boards.some((board) => normalizeImageUrl(board?.imageUrl))}
            className="h-8 px-2.5 rounded-lg border text-xs font-semibold inline-flex items-center gap-1.5 cursor-pointer disabled:opacity-50"
            style={{ borderColor: "var(--color-border-primary)", backgroundColor: SURFACE.card, color: "var(--color-text-primary)" }}
            title={`把已生成的故事板图提交到${assetLibraryLabel(videoProvider)}`}
          >
            {uploadingAllBoardAssets ? <Loader2 size={14} className="animate-spin" /> : <PackagePlus size={14} />}
            一键入库
          </button>}
          <div className="inline-flex items-center gap-1">
            <button
              type="button"
              onClick={generateAllVideosHandler}
              disabled={generatingAllVideos || initialGenerating || !videoGeneratableBoards.length}
              className="h-8 px-2.5 rounded-lg border text-xs font-semibold inline-flex items-center gap-1.5 cursor-pointer disabled:opacity-50"
              style={{ borderColor: "var(--color-border-primary)", backgroundColor: SURFACE.card, color: "var(--color-text-primary)" }}
              title="按顺序提交所有已生成故事板且还没有视频的片段"
            >
              {generatingAllVideos ? <Loader2 size={14} className="animate-spin" /> : <Film size={14} />}
              一键生视频
            </button>
            <EstimatedPoints request={directorVideoCostRequest} compact />
          </div>
          <button
            type="button"
            onClick={downloadAllVideosHandler}
            disabled={downloadingAllVideos || !downloadableVideos.length}
            className="h-8 px-2.5 rounded-lg border text-xs font-semibold inline-flex items-center gap-1.5 cursor-pointer disabled:opacity-50"
            style={{ borderColor: "var(--color-border-primary)", backgroundColor: SURFACE.card, color: "var(--color-text-primary)" }}
            title="下载所有已生成视频，文件名为剧名、集名和片段编号"
          >
            {downloadingAllVideos ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
            下载视频
          </button>
          <div className="inline-flex items-center gap-1">
            <button
              type="button"
              onClick={() => generateAllWithConfirm()}
              title={isDatastartImageProvider(directorImageProvider)
                ? 'DataStart 仅生图；AI 拆段与提示词规划须另行选择文字模型，不自动切换线路；支持单张参考图'
                : isAstraflowImageProvider(directorImageProvider)
                  ? 'Image-2.5 负责生图；AI 拆段与提示词规划沿用当前已选择的文字模型'
                  : undefined}
              disabled={fullGenerationBusy || scriptMissing}
              className="h-8 px-3 rounded-lg border text-xs font-bold inline-flex items-center gap-1.5 cursor-pointer disabled:opacity-50"
              style={{ borderColor: theme.colors.accentPrimary, backgroundColor: theme.colors.accentPrimary, color: "var(--color-accent-contrast)" }}
            >
              {initialGenerating ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
              {hasGeneratedBoards ? "整集重排" : "生成连续故事板"}
            </button>
            <EstimatedPoints request={directorBoardCostRequest} compact />
          </div>
        </div>
      </header>

      {error && (
        <div className="mx-4 mt-3 rounded-md border px-3 py-2 text-sm animate-in fade-in slide-in-from-top-2 duration-200" style={{ borderColor: "var(--color-error)", backgroundColor: "rgba(220,38,38,0.1)", color: "var(--color-error)" }}>
          {error}
        </div>
      )}

      {initialLoading ? (
        <StoryboardBoardLoadingState />
      ) : scriptMissing ? (
        <StoryboardScriptGate projectId={projectId} episodeId={episodeId} onRefresh={loadData} />
      ) : (
      <div className="flex-1 min-h-0 overflow-hidden flex flex-col custom-scrollbar">
        <aside className="order-2 shrink-0 border-t flex min-h-[126px] max-h-[154px]" style={{ borderColor: "var(--color-border-primary)", backgroundColor: SURFACE.panel }}>
          <div className="w-[118px] shrink-0 px-3 py-3 border-r" style={{ borderColor: "var(--color-border-primary)" }}>
            <div className="text-xs font-bold" style={{ color: "var(--color-text-primary)" }}>故事板页</div>
            <div className="text-[11px] mt-1" style={{ color: "var(--color-text-muted)" }}>{boards.length || 0} 页</div>
            <div className="text-[11px] mt-2" style={{ color: "var(--color-text-muted)" }}>
              {downloadableVideos.length}/{boards.length || 0} 视频
            </div>
          </div>
          <div className="flex-1 min-w-0 overflow-x-auto overflow-y-hidden custom-scrollbar p-2">
            <div className="flex gap-2">
              {boards.length ? boards.map((board) => {
                const boardIndex = Number(board?.index);
                const active = boardIndex === Number(activeBoard?.index);
                const imageUrl = normalizeImageUrl(board?.imageUrl);
                const video = boardVideos?.[boardIndex];
                const videoBusy = isBoardVideoBusy(video) || generatingVideoBoardIndexes.has(boardIndex);
                const videoDone = video?.status === "success" && getBoardVideoUrl(video);
                const failed = board?.status === "failed" || video?.status === "failed";
                const boardGenerating = initialGenerating
                  && !failed
                  && !imageUrl
                  && ["pending", "processing", "queued", ""].includes(firstText(board?.status, "pending"));
                return (
                  <button
                    key={`board-nav-${boardIndex || board?.title}`}
                    type="button"
                    onClick={() => setActiveBoardIndex(boardIndex || 1)}
                    className="w-[128px] shrink-0 rounded-lg border p-1.5 text-left cursor-pointer transition-colors"
                    style={{
                      borderColor: active ? theme.colors.accentPrimary : "var(--color-border-primary)",
                      backgroundColor: active ? "var(--color-accent-light)" : SURFACE.card,
                      color: "var(--color-text-primary)",
                      boxShadow: active ? `0 0 0 1px ${theme.colors.accentPrimary}33` : "none",
                    }}
                  >
                    <div className="aspect-video rounded-md overflow-hidden border relative" style={{ borderColor: "var(--color-border-primary)", backgroundColor: SURFACE.soft }}>
                      {imageUrl ? (
                        <img src={imageUrl} alt={board?.title || `故事板页${boardIndex}`} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          {failed ? <AlertTriangle size={16} style={{ color: "var(--color-error)" }} /> : <Film size={16} style={{ color: "var(--color-text-muted)" }} />}
                        </div>
                      )}
                      <span className="absolute left-1 top-1 rounded px-1.5 py-0.5 text-[10px] font-medium" style={{ backgroundColor: "var(--overlay-medium)", color: "#fff" }}>
                        {String(boardIndex || 0).padStart(2, "0")}
                      </span>
                    </div>
                    <div className="mt-1.5 flex items-center justify-between gap-1">
                      <span className="text-[11px] font-bold truncate" style={{ color: active ? theme.colors.accentPrimary : "var(--color-text-primary)" }}>
                        {board?.timeRange || `${Math.max(0, boardIndex - 1) * BOARD_SECONDS}s`}
                      </span>
                      <span className="text-[10px] rounded px-1.5 py-0.5 font-medium" style={{
                        backgroundColor: videoDone ? "rgba(34,197,94,0.12)" : (videoBusy || boardGenerating) ? "var(--color-accent-light)" : failed ? "rgba(220,38,38,0.12)" : SURFACE.soft,
                        color: videoDone ? "var(--color-success)" : (videoBusy || boardGenerating) ? theme.colors.accentPrimary : failed ? "var(--color-error)" : "var(--color-text-muted)",
                      }}>
                        {videoDone ? "视频" : (videoBusy || boardGenerating) ? "生成中" : failed ? "失败" : imageUrl ? "图片" : "待生成"}
                      </span>
                    </div>
                  </button>
                );
              }) : (
                <div className="w-full rounded-md border border-dashed p-3 text-xs text-center" style={{ borderColor: "var(--color-border-primary)", color: "var(--color-text-muted)" }}>
                  暂无故事板页
                </div>
              )}
            </div>
          </div>
        </aside>

        <main className="order-1 flex-1 min-w-0 min-h-0 overflow-hidden p-2.5 min-[1700px]:p-3 flex flex-col gap-3" style={{ backgroundColor: SURFACE.page }}>

          {segmentationMode === "manual" && (
            <ManualSegmentationPanel
              scriptText={scriptText}
              mode={segmentationMode}
              onModeChange={changeSegmentationMode}
              presentationMode={presentationMode}
              segments={normalizedManualSegments}
              onSegmentsChange={setManualSegments}
              disabled={directorBusy}
              duration={BOARD_SECONDS}
              hideHeader
            />
          )}

          {initialGenerating && (
            <section className="shrink-0 rounded-md border p-5 flex items-center gap-3" style={{ borderColor: "var(--color-accent-primary)", backgroundColor: "var(--color-accent-light)" }}>
              <Loader2 size={22} className="animate-spin" style={{ color: theme.colors.accentPrimary }} />
              <div>
                <div className="text-sm font-bold" style={{ color: "var(--color-text-primary)" }}>导演正在生成连续故事板</div>
                <div className="text-sm mt-1" style={{ color: "var(--color-text-muted)" }}>会按剧情段落依次生成，成功一页就先出现在底部页条里；失败项会保留占位。</div>
              </div>
            </section>
          )}

          {boards.length && activeBoard ? (
            <div className="flex-1 min-h-0 animate-in fade-in duration-300">
              <StoryboardBoardListItem
                key={`active-board-${activeBoard.index}`}
                projectId={projectId}
                board={activeBoard}
                boards={boards}
                theme={theme}
                episode={episode}
	                selectedRefs={selectedRefs}
	                aspectRatio={projectDefaultVideoRatio}
	                directorRequest={directorRequest}
	                setDirectorRequest={setDirectorRequest}
	                onSendDirectorMessage={sendDirectorMessageHandler}
	                onOpenReferencePicker={openReferencePickerHandler}
	                onOpenVideoReferencePicker={openVideoReferencePickerHandler}
	                onRemoveSelectedRef={removeSelectedRefHandler}
	                onMatchRefsFromScript={selectMatchedAssetsHandler}
	                onClearSelectedRefs={clearRefsHandler}
	                pendingDirectorAction={pendingDirectorAction}
                onExecutePendingSingleBoard={executePendingSingleBoardHandler}
                onExecutePendingAllBoards={executePendingAllBoardsHandler}
                onKeepPendingAsNote={keepPendingAsNoteHandler}
                onCancelPendingAction={cancelPendingDirectorAction}
                directorBusy={directorBusy}
                loading={loading}
                hasGeneratedBoards={hasGeneratedBoards}
                presentationMode={presentationMode}
                onPreview={setPreviewImage}
                boardImageHistory={boardImageHistories?.[Number(activeBoard?.index)] || []}
                onSelectBoardImageHistory={selectBoardImageHistory}
                onRegenerate={regenerateBoardHandler}
                regenerating={regeneratingBoardIndexes.has(Number(activeBoard?.index))}
                onGenerateVideo={generateVideoFromBoardHandler}
                onStopVideo={stopVideoGenerationHandler}
                onEraseSubtitle={eraseSubtitleForBoardHandler}
                onUploadPackageItem={uploadPackageItemToGuaAssetHandler}
                onUploadBoardAsset={uploadBoardAssetHandler}
                videoProvider={videoProvider}
                videoProviderLabel={videoProviderLabel}
                showGuaAssetUpload={assetIngestSupported}
                videoDraft={{
                  ...(videoDrafts?.[Number(activeBoard?.index)] || {}),
                  resolution: firstText(videoDrafts?.[Number(activeBoard?.index)]?.resolution, globalVideoResolution, GUA_VIDEO_RESOLUTION),
                }}
                onVideoDraftChange={updateVideoDraft}
                videoOptionsOpen={openVideoOptionsIndexes.has(Number(activeBoard?.index))}
                onToggleVideoOptions={toggleVideoOptions}
                onSelectVideoHistory={selectBoardVideoHistory}
                onDownloadVideoHistory={downloadBoardVideoHistoryHandler}
                videoTask={boardVideos?.[Number(activeBoard?.index)]}
                videoGenerating={generatingVideoBoardIndexes.has(Number(activeBoard?.index))}
                assetMetaMap={assetMetaMap}
                uploadingAssetUrls={uploadingAssetUrls}
                disabled={directorBusy}
                focusColumnPercents={focusColumnPercents}
                onFocusColumnPercentsChange={updateFocusColumnPercents}
                focusMode
              />

            </div>
          ) : (
            <section className="flex-1 min-h-0 grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_360px] gap-3">
              <div className="min-h-[340px] rounded-md border border-dashed flex flex-col items-center justify-center text-center px-6" style={{ borderColor: "var(--color-border-primary)", backgroundColor: SURFACE.panel }}>
                <Film size={42} style={{ color: theme.colors.accentPrimary }} />
                <div className="text-base font-bold mt-4" style={{ color: "var(--color-text-primary)" }}>还没有生成故事板页</div>
                <div className="text-sm leading-6 mt-2 max-w-[620px]" style={{ color: "var(--color-text-muted)" }}>
                  先在右侧选好本集人物、场景、道具参考图。生成时会把这些参考图作为整集连续性锚点一起传给导演。
                </div>
                <div className="mt-5 flex flex-wrap items-center justify-center gap-2">
                  <button
                    type="button"
                    onClick={selectMatchedAssetsHandler}
                    disabled={directorBusy}
                    className="h-9 px-3 rounded-lg border text-xs font-bold inline-flex items-center gap-1.5 cursor-pointer disabled:opacity-50"
                    style={{ borderColor: "var(--color-border-primary)", backgroundColor: SURFACE.card, color: "var(--color-text-primary)" }}
                  >
                    <Sparkles size={14} />
                    按剧本匹配
                  </button>
                  <div className="inline-flex items-center gap-1">
                    <button
                      type="button"
                      onClick={() => generateAllWithConfirm()}
                      disabled={fullGenerationBusy || scriptMissing}
                      className="h-9 px-4 rounded-lg border text-xs font-bold inline-flex items-center gap-1.5 cursor-pointer disabled:opacity-50"
                      style={{ borderColor: theme.colors.accentPrimary, backgroundColor: theme.colors.accentPrimary, color: "var(--color-accent-contrast)" }}
                    >
                      {initialGenerating ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
                      生成连续故事板
                    </button>
                    <EstimatedPoints request={directorBoardCostRequest} compact />
                  </div>
                </div>
              </div>
              <SelectedRefsPanel
                selectedRefs={selectedRefs}
                onRemoveRef={removeSelectedRefHandler}
                onOpenPicker={openReferencePickerHandler}
                onMatchFromScript={selectMatchedAssetsHandler}
                onClear={clearRefsHandler}
                disabled={directorBusy}
                limit={referenceLimit}
                embedded
                className="min-h-[340px]"
              />
            </section>
          )}
        </main>

      </div>
      )}

      {previewImage && (
        <PreviewModal
          image={previewImage}
          onClose={() => setPreviewImage(null)}
          hasPrev={currentPreviewPosition > 0}
          hasNext={currentPreviewPosition >= 0 && currentPreviewPosition < previewableBoards.length - 1}
          onPrev={() => showPreviewAt(currentPreviewPosition - 1)}
          onNext={() => showPreviewAt(currentPreviewPosition + 1)}
        />
      )}

      <AssetPickerModal
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        assets={assets}
        selectedKeys={selectedRefKeys}
        limit={referenceLimit}
        disableToggle={directorBusy}
        onToggle={toggleAssetHandler}
        onClear={clearRefs}
        onMatchFromScript={selectMatchedAssets}
      />
      <AssetPickerModal
        open={Boolean(videoReferencePickerBoardIndex)}
        onClose={() => setVideoReferencePickerBoardIndex(null)}
        assets={assets}
        selectedKeys={videoReferencePickerSelectedKeys}
        limit={videoReferencePickerAssetLimit}
        disableToggle={directorBusy}
        onToggle={toggleVideoReferenceAsset}
        onClear={clearVideoReferenceAssets}
        additionalControls={supportsSeedance25MediaReferences(videoProvider) ? (
          <label className="inline-flex items-center gap-2 text-sm cursor-pointer">
            {uploadingVideoReferences ? "正在上传参考素材…" : "上传图片 / 视频 / 音频参考"}
            <input type="file" accept="image/*,video/*,audio/*" multiple className="hidden"
              disabled={directorBusy || uploadingVideoReferences}
              onChange={(event) => { uploadVideoReferenceFiles(event.target.files); event.target.value = ""; }} />
          </label>
        ) : null}
      />
    </div>
  );
};

export default EpisodeStoryboardBoardWorkbench;
